import { setCaptionsHidden } from '../services/karaoke/captionsVisibility';
import { SceneService } from '../services/SceneService';
import { normalizeLangKey } from '../utility/bcp47';
import { LogManager } from '../utility/Logger';
import { VideoEditorLogger } from '../utility/VideoEditorLogger';
import {
  CreateTrackCommand,
  CropTimelineCommand,
  CropTimelineToReelCommand,
  RemoveTrackCommand,
  ResizeElementRippleAllTracksCommand,
  RestoreTranscriptFromOriginalCommand,
  SetTimelineDurationCommand,
  ShiftElementCommand,
  ShiftMultipleElementsCommand,
  SplitElementCommand,
  TimelineMode,
  TrackLayerDownCommand,
  TrackLayerUpCommand,
  TranscriptPasteCommand,
  TranscriptRemoveCommand,
  TranscriptRestoreCommand,
  TrimElementEndCommand,
  TrimElementStartCommand,
} from './commands';
import { AddCompositionElementCommand } from './commands/AddCompositionElementCommand';
import { AddElementCommand } from './commands/AddElementCommand';
import { AddMediaElementCommand } from './commands/AddMediaElementCommand';
import { AddMusicElementCommand } from './commands/AddMusicElementCommand';
import { AddSceneCommand } from './commands/AddSceneCommand';
import { AddShapeElementCommand } from './commands/AddShapeElementCommand';
import { AddTextElementCommand } from './commands/AddTextElementCommand';
import { AddVideoElementCommand } from './commands/AddVideoElementCommand';
import { ChangeAspectRatioCommand } from './commands/ChangeAspectRatioCommand';
import { CreateScenesByMediaTimeCommand } from './commands/CreateScenesByMediaTimeCommand';
import { AddKaraokeBreakCommand } from './commands/karaoke/AddKaraokeBreakCommand';
import { AddSyntheticCaptionBreakCommand } from './commands/karaoke/AddSyntheticCaptionBreakCommand';
import { AdjustKaraokeClipTimeCommand } from './commands/karaoke/AdjustKaraokeClipTimeCommand';
import { AdjustSyntheticCaptionBreakCommand } from './commands/karaoke/AdjustSyntheticCaptionBreakCommand';
import { ClearSyntheticCaptionBreaksCommand } from './commands/karaoke/ClearSyntheticCaptionBreaksCommand';
import { EditDubCaptionRowsCommand } from './commands/karaoke/EditDubCaptionRowsCommand';
import { HideInKaraokeCommand } from './commands/karaoke/HideInKaraokeCommand';
import {
  captionClipsCrossSpeakers,
  MergeAdjacentCaptionClipsCommand,
} from './commands/karaoke/MergeAdjacentCaptionClipsCommand';
import { ProduceKaraokeCommand } from './commands/karaoke/ProduceKaraokeCommand';
import { RemoveKaraokeBreakCommand } from './commands/karaoke/RemoveKaraokeBreakCommand';
import { RemoveSyntheticCaptionBreakCommand } from './commands/karaoke/RemoveSyntheticCaptionBreakCommand';
import { ShowInKaraokeCommand } from './commands/karaoke/ShowInKaraokeCommand';
import { SourceSetScopeCommand } from './commands/karaoke/SourceSetScopeCommand';
import { SyncTranslatedCaptionTextCommand } from './commands/karaoke/SyncTranslatedCaptionTextCommand';
import { ToggleCaptionSoftBreakCommand } from './commands/karaoke/ToggleCaptionSoftBreakCommand';
import { UpdateKaraokeConfigCommand } from './commands/karaoke/UpdateKaraokeConfigCommand';
import { MoveElementToTrackCommand } from './commands/MoveElementToTrackCommand';
import { InsertReelSectionsCommand } from './commands/reel/InsertReelSectionsCommand';
import { MoveReelChapterCommand } from './commands/reel/MoveReelChapterCommand';
import { ReorderReelSectionsCommand } from './commands/reel/ReorderReelSectionsCommand';
import { MoveReelSectionCommand } from './commands/reel/MoveReelSectionCommand';
import { RemoveReelSectionCommand } from './commands/reel/RemoveReelSectionCommand';
import { ReplaceReelSectionCommand } from './commands/reel/ReplaceReelSectionCommand';
import { RemoveElementCommand } from './commands/RemoveElementCommand';
import { RemoveSceneCommand } from './commands/RemoveSceneCommand';
import { SplitSceneCommand } from './commands/SplitSceneCommand';
import { ChangeElementPlacementsCommand } from './commands/timeline/ChangeElementPlacementsCommand';
import { TranscriptAddPunctuationCommand } from './commands/transcript/TranscriptAddPunctuationCommand';
import { TranscriptMultiRangeTextReplaceCommand } from './commands/transcript/TranscriptMultiRangeTextReplaceCommand';
import { TranscriptMultiSegmentTextReplaceCommand } from './commands/transcript/TranscriptMultiSegmentTextReplaceCommand';
import { TranscriptSpeakerReassignCommand } from './commands/transcript/TranscriptSpeakerReassignCommand';
import { TranscriptTextReplaceCommand } from './commands/transcript/TranscriptTextReplaceCommand';
import { UpdateElementCommand } from './commands/UpdateElementCommand';
import { UpdateMultipleElementsCommand } from './commands/UpdateMultipleElementsCommand';
import { UpdateSceneCommand } from './commands/UpdateSceneCommand';
import { UpdateScenesCommand } from './commands/UpdateScenesCommand';
import { UpdateTimelineAndElementsCommand } from './commands/UpdateTimelineAndElementsCommand';
import { DubDescriptionStore } from './dubbing/DubDescriptionStore';
import { resolveHeardDub, selectCaptionBasis } from './dubbing/heardDub';
import { readOriginalVideo } from './dubbing/readOriginalVideo';
import { CommandManager } from './lib/CommandManager';
import { KaraokeTrackManager } from './lib/KaraokeTrackManager';
import { ObserverManager } from './lib/ObserverManager';
import { PlacementReconciler } from './lib/PlacementReconciler';
import { TrackManager } from './lib/TrackManager';
import { TranscriptTrackManager } from './lib/TranscriptTrackManager';
import { VideoElement } from './models';
import { Element } from './models/Element';
import { RemovedTranscriptClip } from './models/elements/RemovedTranscriptClip';
import { VideoTranscriptElement } from './models/elements/VideoTranscriptElement';
import {
  composeCaptionSets,
  splitCaptionSets,
} from './models/karaoke/captionSets';
import { cloneKaraokeConfigs } from './models/karaoke/cloneKaraokeConfigs';
import {
  DEFAULT_EDITOR_KARAOKE_CONFIG,
  type KaraokeConfig,
} from './models/karaoke/KaraokeConfig';
import { KaraokeTrack } from './models/karaoke/KaraokeTrack';
import { Scene } from './models/layout';
import { LegacyTranscript } from './models/LegacyTranscript';
import { KARAOKE_TRACK_NUMBER, Timeline } from './models/Timeline';
import { Transcript } from './models/Transcript';
import { TranscriptTrack } from './models/TranscriptTrack';
import {
  buildDubRecord,
  dubCaptionInputs,
  dubCaptionSplitPoint,
  dubRowOptions,
  joinDubRows,
  setDubRowOffsets,
  splitDubRow,
} from './services/dubCaptionRows';
import { IdGenerator, IdGeneratorEntityType } from './services/IdGenerator';
import { fillInWhitespacesInTranscriptElements } from './services/punctuation';
import {
  TranscriptValidationError,
  verifyTranscriptConsistencyQuick,
} from './services/transcriptValidations';
import { EditorChangeType } from './types';

import type {
  FaceDetection,
  ISceneFramingService,
} from '../services/framing/SmartFramingService';
import type {
  TranscriptElement,
  TranscriptElementLegacy,
} from '../types.ts/transcript';
import type { Command } from './commands';
import type { AddMusicElementOptions } from './commands/AddMusicElementCommand';
import type { AddVideoElementOptions } from './commands/AddVideoElementCommand';
import type { ElementPlacement } from './commands/timeline/ChangeElementPlacementsCommand';
import type { SegmentTextChange } from './commands/transcript/TranscriptMultiRangeTextReplaceCommand';
import type { TimelineUpdates } from './commands/UpdateTimelineAndElementsCommand';
import type { ExternalHeardDub, HeardDub } from './dubbing/heardDub';
import type {
  IVideoEditor,
  TranslationPhraseResolver,
} from './interface/IVideoEditor';
import type { KnownPunctuation } from './interface/IVideoEditorCommands';
import type { RippleConfig } from './lib/TrackManager';
import type { HasClipTranscriptSegment } from './models/elements/HasClipTranscriptSegment';
import type { KaraokeClip } from './models/karaoke/KaraokeClip';
import type { IScene } from './models/layout/types';
import type { TimelineJSON } from './models/Timeline';
import type { Track } from './models/Track';
import type { CaptionSoftBreakAnchor } from './services/captionSoftBreaks';
import type {
  ActiveTranscriptTokenWithClipSegmentAttributes,
  EditorObserver,
} from './types';
import type { NaturalDimensions, VideoDimensions } from './types/aspectRatio';
import type { KaraokeData, KaraokeLine } from './types/karaoke';
import type { SceneDetection } from './types/sceneDetection';
import type { TranscriptClipboardEntry } from './types/transcriptClipboard';
import type { IKaraokePresetProvider } from '@src/services/karaoke/KaraokePresetProvider';
import type { IKaraokeTemplateVariantService } from '@src/services/karaoke/KaraokeTemplateVariantService';
import type { ElementClipboardEntry } from '@src/services/timeline/editing/ElementClipboardTypes';
import type {
  PunchListItem as PunchListItemData,
  Video,
} from '@src/types.ts/story';
import type { TranslationPhrase } from '@src/types.ts/translation';

type KnownFlags =
  | 'enableTranscriptMigrationValidation'
  | 'reorderLayoutCompositionTrackToBottom';

const logger = LogManager.getLogger('editor:VideoEditor');

export interface CutTranscriptSegment {
  elementId: string;
  initialIndexes: number[];
}

export interface VideoEditorConfig {
  maxMediaDurationToTrim: number;
}

export interface PasteTranscriptSegment {
  intoTime: number;
  startInitialIndex: number;
  endInitialIndex: number;
}

export interface RemovedSegment {
  fromTs: number;
  toTs: number;
}

const DEFAULT_MAX_MEDIA_DURATION_TO_TRIM = 1.5;
const DEFAULT_SCENE_SNAP_THRESHOLD_FRAMES = 5;

export class VideoEditor implements IVideoEditor {
  // Core state
  timeline: Timeline;

  /**
   * The resolved dub description, per language, held in memory (PLAT-11511).
   *
   * The dub is a derived value now: nothing about it is stored on the timeline
   * except three options, and the dub engine builds the audio afresh every time
   * a document is produced. That engine runs inside a synchronous method, so it
   * cannot fetch, and the addresses it needs are signed and stop working after
   * an hour, so they must not be saved. This holder is where the surfaces above
   * leave what they fetched, and it is the only place the engine reads from.
   *
   * It outlives the timeline on purpose: loading a story replaces the timeline
   * object, and the descriptions in hand are still the right ones.
   */
  readonly dubSource = new DubDescriptionStore();
  /**
   * PLAT-11906 — whether the Both voice position shows the dub-timed captions.
   * Off everywhere except the review editors (the review's hear-both); Clip
   * Preview, Creator Studio and publish keep source-timed captions under Both.
   */
  private bothFollowsDub = false;
  /** PLAT-11906 — the dub the story-entry review plays itself, or `null`. */
  private externalHeardDub: ExternalHeardDub | null = null;
  legacyTranscript?: LegacyTranscript;
  private karaokeConfigs: Map<string, KaraokeConfig> = new Map();
  private defaultKaraokeConfigId: string | null = null;
  // PLAT-9621: optional callback that resolves translation phrases for the
  // captions producer. Registered by the translation hydration hook from
  // outside the editor so this layer stays free of store imports.
  private translationPhraseResolver?: TranslationPhraseResolver;
  private originalTranscript: Transcript;
  private commandManager: CommandManager;
  private trackManager: TrackManager;
  private transcriptTrackManager: TranscriptTrackManager; // manager for transcript track
  private transcriptTrackId?: string; // New property to store ID of track with transcript
  private pendingPunchListItems: PunchListItemData[] | null = null; // Store items until transcript is loaded
  private _shouldCreateUndoRedoEntry: boolean = true;

  /**
   * Optional listener that will be called every time a command is successfully executed.
   * The listener can be used by host applications (e.g. VideoCreatorStore) to hook into
   * the editor's internal command execution and maintain a consolidated undo/redo stack.
   */
  public commandExecutedListener?: (command: Command) => void;

  // State management
  private batchDepth: number = 0; // Counter for backward compatibility
  private inBatch: boolean = false; // Simpler flag for batch operations
  private dirty: boolean = false; // Flag to track if timeline needs syncing
  private _isSyncingToRenderer: boolean = false; // For compatibility with existing code

  // Cut Paste
  // @deprecated
  private cutInitialIndexes: number[] = [];

  private cutPasteClipboardEntries: TranscriptClipboardEntry[] | null = null;

  // Element copy/paste clipboard
  private _elementClipboard: ElementClipboardEntry | null = null;

  // Timeline editing mode
  private timelineMode: TimelineMode = TimelineMode.RIPPLE;

  // Observer pattern implementation (moved to ObserverManager)
  private observerManager: ObserverManager;

  private flags: Record<KnownFlags, boolean> = {
    enableTranscriptMigrationValidation: false,
    reorderLayoutCompositionTrackToBottom: false,
  };

  private config: VideoEditorConfig = {
    maxMediaDurationToTrim: DEFAULT_MAX_MEDIA_DURATION_TO_TRIM,
  };

  // Legacy event handler (kept for backward compatibility)
  onStateChange?: (timeline: Timeline) => void;

  constructor() {
    this.timeline = new Timeline();
    this.timeline.attachDubSource(this.dubSource);
    // PLAT-11905 — a description arriving (or going) is the moment the dubbed
    // voice starts (or stops) being heard, so the captions switch set in the
    // same pass. The holder lives and dies with this editor.
    this.dubSource.onChange(() =>
      this.syncCaptionSetToVoice({ reconcile: true }),
    );
    this.commandManager = new CommandManager(this);
    this.observerManager = new ObserverManager(this);

    // Create TrackManager with this instance as the editor
    this.trackManager = new TrackManager(this);
    this.transcriptTrackManager = new TranscriptTrackManager(
      this,
      this.trackManager,
    );

    this.originalTranscript = new Transcript([]);
    // Initialize editor logging
    this.initializeLogging();
  }

  /**
   * Initialize editor logging settings
   * This can be called again to change logging configuration
   */
  initializeLogging(
    options: {
      enableCommandLogging?: boolean;
      enableTimelineLogging?: boolean;
      enableTranscriptLogging?: boolean;
      enableBatchLogging?: boolean;
      enablePerformanceLogging?: boolean;
      enableStateLogging?: boolean;
    } = {},
  ): void {
    // Check if we're in development environment
    const isDev = process.env.NODE_ENV === 'development';

    VideoEditorLogger.initialize({
      // In dev mode, enable most channels by default unless explicitly disabled
      commandsEnabled: options.enableCommandLogging ?? true,
      timelineEnabled: options.enableTimelineLogging ?? isDev,
      transcriptEnabled: options.enableTranscriptLogging ?? isDev,
      batchingEnabled: options.enableBatchLogging ?? isDev,
      performanceEnabled: options.enablePerformanceLogging ?? isDev,
      stateEnabled: options.enableStateLogging ?? isDev,
    });

    VideoEditorLogger.logStateChange('editor_initialized', {
      timelineId: this.timeline.id,
      hasTranscript: !!this.legacyTranscript,
      hasNewArchitectureTranscript: !!this.originalTranscript,
      environment: process.env.NODE_ENV,
      isDev,
    });
  }

  /**
   * Gets the track manager instance
   * @returns The TrackManager instance
   */
  getTrackManager(): TrackManager {
    return this.trackManager;
  }

  /**
   * Gets the transcript track manager instance
   * @returns The TranscriptTrackManager instance
   */
  getTranscriptTrackManager(): TranscriptTrackManager {
    return this.transcriptTrackManager;
  }

  /**
   * Gets the current timeline mode
   */
  getTimelineMode(): TimelineMode {
    return this.timelineMode;
  }

  /**
   * Sets the timeline mode
   */
  setTimelineMode(mode: TimelineMode): void {
    this.timelineMode = mode;
  }

  /**
   * Gets the current framerate
   */
  getFramerate(): number {
    return this.timeline.framerate;
  }

  /**
   * Sets the framerate
   */
  setFramerate(fps: number): void {
    this.timeline.setFramerate(fps);
    this.markDirty('framerate_change');

    // Notify observers about the framerate change
    this.notifyObservers(EditorChangeType.FRAMERATE_CHANGED, {
      fps: fps,
      previousFps: this.timeline.framerate,
    });
    // Also notify of general timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'framerate_change',
      fps: fps,
    });

    this.maybeSync();
  }

  /**
   * Gets the current snap-to-frame setting
   */
  getSnapToFrame(): boolean {
    return this.timeline.snapToFrame;
  }

  /**
   * Sets the snap-to-frame setting
   */
  setSnapToFrame(snapToFrame: boolean): void {
    const previousValue = this.timeline.snapToFrame;
    this.timeline.setSnapToFrame(snapToFrame);
    this.markDirty('snap_to_frame_change');

    // Notify observers about the snap setting change
    this.notifyObservers(EditorChangeType.SNAP_TO_FRAME_CHANGED, {
      snapToFrame: snapToFrame,
      previousValue: previousValue,
    });
    // Also notify of general timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'snap_to_frame_change',
      snapToFrame: snapToFrame,
    });

    this.maybeSync();
  }

  /**
   * Converts time in seconds to frame number based on the current framerate
   */
  timeToFrame(time: number): number {
    return this.timeline.timeToFrame(time);
  }

  /**
   * Converts frame number to time in seconds based on the current framerate
   */
  frameToTime(frame: number): number {
    return this.timeline.frameToTime(frame);
  }

  /**
   * Snaps time to the nearest frame boundary if snap-to-frame is enabled
   */
  snapTimeToFrame(time: number): number {
    return this.timeline.snapTimeToFrame(time);
  }

  /**
   * Executes a command and adds it to history
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  executeCommand(command: Command): boolean {
    return this.executeInBatch(() => {
      const startTime = performance.now();
      const result = this.commandManager.execute(command);
      const endTime = performance.now();

      // Log the execution result
      if (result) {
        VideoEditorLogger.logCommand(command, 'complete', {
          durationMs: endTime - startTime,
        });

        // Notify external listener (e.g., host application) that a command has been executed
        if (this.commandExecutedListener) {
          try {
            this.commandExecutedListener(command);
          } catch (err) {
            // Listener errors should not break editor functionality
            VideoEditorLogger.logCommand(command, 'error', { err });
          }
        }
      } else {
        VideoEditorLogger.logCommand(command, 'error');
      }

      return result;
    });
  }

  /**
   * Undoes the last command
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  undo(): boolean {
    console.warn(
      'Undo from headless not ready yet, videoCreator.undo() should be used instead',
    );
    const command = this.commandManager.getLastExecutedCommand();
    if (!command) {
      VideoEditorLogger.logStateChange('undo', { error: 'No command to undo' });
      return false;
    }

    return this.executeInBatch(() => {
      const result = this.commandManager.undo();
      VideoEditorLogger.logCommandUndo(command, result);
      return result;
    });
  }

  /**
   * Redoes a previously undone command
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  redo(): boolean {
    console.warn(
      'Redo from headless not ready yet, videoCreator.redo() should be used instead',
    );
    const command = this.commandManager.getNextUndoneCommand();
    if (!command) {
      VideoEditorLogger.logStateChange('redo', { error: 'No command to redo' });
      return false;
    }

    return this.executeInBatch(() => {
      const result = this.commandManager.redo();
      VideoEditorLogger.logCommandRedo(command, result);
      return result;
    });
  }

  /**
   * Checks if there are any commands that can be undone
   * @returns true if undo is available, false otherwise
   */
  canUndo(): boolean {
    return this.commandManager.getLastExecutedCommand() !== null;
  }

  /**
   * Checks if there are any commands that can be redone
   * @returns true if redo is available, false otherwise
   */
  canRedo(): boolean {
    return this.commandManager.getNextUndoneCommand() !== null;
  }

  /**
   * Clears the internal undo/redo history maintained by the CommandManager.
   * This is used by host applications (e.g. legacy VideoCreatorStore) when
   * they revert the full video state via snapshots.  After such a revert the
   * existing command history inside the headless editor is no longer valid,
   * so we wipe it to avoid allowing the user to redo commands that no longer
   * apply to the current timeline.
   */
  clearHistory(): void {
    this.commandManager.clearHistory();
  }

  /**
   * Registers an observer for specific change types
   * Delegates to ObserverManager
   * @param observer The observer to register
   * @param changeTypes The types of changes to observe (if none provided, observes all changes)
   */
  registerObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    this.observerManager.registerObserver(observer, changeTypes);
  }

  /**
   * Unregisters an observer from specific change types
   * Delegates to ObserverManager
   * @param observer The observer to unregister
   * @param changeTypes The types of changes to unobserve (if none provided, unobserves all changes)
   */
  unregisterObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    this.observerManager.unregisterObserver(observer, changeTypes);
  }

  /**
   * Notifies all registered observers about a change
   * Delegates to ObserverManager
   * @param changeType The type of change that occurred
   * @param payload Optional data associated with the change
   * @param immediate Optional flag to force immediate notification even during batching (use with caution)
   */
  notifyObservers(
    changeType: EditorChangeType,
    payload?: any,
    immediate: boolean = false,
  ): void {
    this.observerManager.notifyObservers(changeType, payload, immediate);

    // If this is a timeline-related change but not the general TIMELINE_CHANGED event,
    // also emit a TIMELINE_CHANGED event to ensure MobX reactivity is triggered
    if (
      changeType !== EditorChangeType.TIMELINE_CHANGED &&
      [
        EditorChangeType.ELEMENT_ADDED,
        EditorChangeType.ELEMENT_REMOVED,
        EditorChangeType.ELEMENT_UPDATED,
        EditorChangeType.ELEMENT_SHIFTED,
        EditorChangeType.ELEMENT_TRACK_CHANGED,
        EditorChangeType.ELEMENT_TRIMMED_START,
        EditorChangeType.ELEMENT_TRIMMED_END,
        EditorChangeType.TRACK_ADDED,
        EditorChangeType.TRACK_REMOVED,
        EditorChangeType.TRANSCRIPT_LOADED,
        EditorChangeType.KARAOKE_LOADED,
      ].includes(changeType)
    ) {
      this.observerManager.notifyObservers(
        EditorChangeType.TIMELINE_CHANGED,
        {
          source: changeType,
          details: payload,
        },
        immediate,
      );
    }
  }

  /**
   * Checks if sync is needed and performs it if necessary
   * Only syncs if not in a batch operation and editor is dirty
   * Also processes any queued notifications after syncing
   */
  private maybeSync(): void {
    if (!this.isInBatch() && this.dirty) {
      this.dirty = false;

      // Process any pending notifications after syncing
      this.observerManager.processQueuedNotifications();
    }
  }

  /**
   * Returns the current batch depth
   * Exposed for CommandManager to use
   */
  getBatchDepth(): number {
    return this.batchDepth;
  }

  /**
   * Returns whether we're currently in a batch operation
   * Replacement for getBatchDepth() > 0 checks
   */
  isInBatch(): boolean {
    return this.batchDepth > 0;
  }

  /**
   * Executes an operation within a batch, ensuring proper begin/end batch handling
   * This is the preferred way to execute operations that need to be batched
   * @param operation The function to execute within the batch
   * @returns The result of the operation
   */
  executeInBatch<T>(operation: () => T): T {
    const wasTopLevelBatch = this.batchDepth === 0;

    // Start the batch if this is a top-level call
    if (wasTopLevelBatch) {
      this.beginBatch();
    }

    try {
      // Execute the operation
      return operation();
    } finally {
      // End the batch if we started it
      if (wasTopLevelBatch) {
        this.endBatch();
      }
    }
  }

  /**
   * Marks the timeline as dirty and requiring sync
   * Centralized method to ensure consistent behavior
   * Made public for CommandManager to use
   * @param source Descriptive source of the change for logging
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  markDirty(source: string = 'unknown'): void {
    this.dirty = true;
  }
  //   if (!this.dirty) {
  //     // Replace console log with structured logging
  //     VideoEditorLogger.logStateChange('timeline_dirty', { source });

  //     this.dirty = true;
  //   }
  // }

  private isTranscriptTrackSubstitutableWith(track: Track): boolean {
    const currentVideoTranscriptTrack = this.getVideoTranscriptTrack();
    if (!currentVideoTranscriptTrack) return false;
    if (track.type !== 'video') return false;
    const currentTrackElements = currentVideoTranscriptTrack.elements;
    const newTrackElements = track.elements;

    if (currentTrackElements.length !== newTrackElements.length) return false;
    return currentTrackElements.every((curEl, pos) => {
      const newEl = newTrackElements[pos];
      return (
        newEl instanceof VideoElement &&
        Math.abs(curEl.time - newEl.time) < 1e-4 &&
        Math.abs(curEl.durationSeconds - newEl.durationSeconds) < 1e-4 &&
        Math.abs(curEl.getMediaStartTime() - newEl.getMediaStartTime()) < 1e-4
      );
    });
  }

  private makeNewTranscriptTrackWithElementsFrom(track: Track) {
    const currentVideoTranscriptTrack = this.getVideoTranscriptTrack();

    if (currentVideoTranscriptTrack) {
      let videoElementIdx = -1;
      const mergedSegmentElements =
        currentVideoTranscriptTrack.transcriptSegmentElements.map(el => {
          if (el instanceof VideoTranscriptElement) {
            videoElementIdx += 1;
            return new VideoTranscriptElement({
              ...track.elements[videoElementIdx].getState(),
              clipTranscriptSegment: el.getClipTranscriptSegment(),
            });
          } else if (el instanceof RemovedTranscriptClip) {
            return new RemovedTranscriptClip({
              clipTranscriptSegment: el.clipTranscriptSegment,
            });
          }
          return el;
        });

      return new TranscriptTrack({
        id: track.id,
        name: track.name,
        type: 'video',
        transcriptSegmentElements: mergedSegmentElements,
      });
    }
    return track; // fallback
  }

  resetState() {
    this.timeline = new Timeline();
    this.timeline.attachDubSource(this.dubSource);
    this.transcriptTrackId = undefined;
  }

  exportVideoSources(): {
    videoSource: TimelineJSON;
    transcript: readonly TranscriptElementLegacy[];
    originalTranscription: readonly TranscriptElementLegacy[] | undefined;
    karaokeData: KaraokeData | null;
    scenes: readonly IScene[] | null;
  } {
    const videoSource = this.timeline.toStoredJSON();
    const transcript = this.getFullTranscript();
    const originalTranscription = this.getOriginalTranscript();

    const karaokeData = this.getCaptionData() ?? {
      clips: [],
      configs: this.getKaraokeConfigs(),
      defaultConfigId: this.getDefaultKaraokeConfig()?.id || null,
    };
    const scenes = this.timeline.scenes.map(s => s.toJSON());

    return {
      videoSource,
      transcript,
      originalTranscription,
      karaokeData,
      scenes,
    };
  }

  /**
   * Loads a video source
   */
  loadVideoSource(
    json: Record<string, any>,
    originalVideoDuration?: string,
  ): void {
    this.timeline = Timeline.fromJSON(json);
    // Re-attached because loading replaces the timeline object, while the
    // descriptions the holder carries are still the right ones.
    this.timeline.attachDubSource(this.dubSource);

    // Normalize legacy single-chapter videos
    this.normalizeSingleChapterVideo(originalVideoDuration);

    // Convert marked logos to LogoElement instances and identify coupling
    this.trackManager.convertMarkedLogosToLogoElements();
    this.trackManager.identifyAndMarkCoupledElements();

    this.dirty = true;

    // Notify observers
    this.notifyObservers(EditorChangeType.PROJECT_LOADED, {
      timeline: this.timeline,
      source: json,
    });

    this.maybeSync();
  }

  /**
   * Normalizes legacy single-chapter videos that incorrectly have trim properties.
   * Checks all video elements and normalizes any where trim_duration equals the full original video duration.
   * This indicates the trim is a single-chapter artifact rather than an intentional user trim.
   * Works even after splits/excisions.
   */
  private normalizeSingleChapterVideo(originalVideoDuration?: string): void {
    if (!originalVideoDuration) {
      return; // Can't normalize without knowing the original duration
    }

    // Parse the original video duration (e.g., "1956.92 s" -> 1956.92)
    const parsedOriginalDuration = Element.parseTimeProperty(
      originalVideoDuration,
    );
    if (typeof parsedOriginalDuration !== 'number') {
      return; // Invalid duration format
    }

    // Get all video elements across all tracks
    const allVideoElements = this.timeline.allElements.filter(
      el => el.type === 'video',
    );

    // Check each video element for single-chapter artifact signature
    for (const videoEl of allVideoElements) {
      const trimDuration = videoEl.trim_duration;

      // Normalize if:
      // 1. Has trim_duration defined
      // 2. trim_duration matches the full original video duration (within small tolerance)
      // This indicates a legacy single-chapter video artifact, not an intentional trim
      if (
        trimDuration !== null &&
        Math.abs(trimDuration - parsedOriginalDuration) < 0.01
      ) {
        logger.info(
          'Normalizing single-chapter video segment: removing trim properties',
          {
            elementId: videoEl.id,
            trackId: videoEl.track,
            trimDuration,
            originalVideoDuration: parsedOriginalDuration,
          },
        );

        videoEl.update({ trim_duration: null });
      }
    }
  }

  /**
   * Repairs a saved video whose scenes bind a slot to a track other than the
   * transcript track.
   *
   * Every non-karaoke slot binds the main video track
   * (`LayoutSegmentFactory.createBindings`), so any other binding is a bad write
   * — load-time track renumbering once remapped bindings through a map keyed in
   * the wrong numbering, and a save persisted the result. Left in, the layout
   * wraps whatever occupies that track and the video gets no representation.
   *
   * Idempotent; karaoke bindings are left alone.
   */
  private normalizeSceneBindingsToTranscriptTrack(trackId: string): void {
    let repaired = false;
    const scenes = this.timeline.scenes.map(scene => {
      const staleSlots = Object.entries(scene.layout.bindings).filter(
        ([slot, binding]) => slot !== 'karaoke' && binding.trackId !== trackId,
      );
      if (staleSlots.length === 0) return scene;

      logger.error('Repairing scene bound off the transcript track', {
        sceneId: scene.id,
        transcriptTrackId: trackId,
        staleBindings: Object.fromEntries(
          staleSlots.map(([slot, binding]) => [slot, binding.trackId]),
        ),
      });

      const layout = scene.layout.clone();
      for (const [slot, binding] of staleSlots) {
        layout.bindTrack(slot, { ...binding, trackId });
      }
      repaired = true;
      return new Scene({
        id: scene.id,
        anchorStart: scene.anchorStart,
        anchorEnd: scene.anchorEnd,
        layout,
      });
    });

    if (repaired) {
      this.timeline.setScenes(scenes);
    }
  }

  /**
   * Loads a transcript into the editor
   * @param transcriptElements Current project state transcript elements to load
   * @param originalTranscriptElements Original transcript elements to load
   */
  loadTranscript(
    transcriptElements: TranscriptElementLegacy[],
    originalTranscriptElements: TranscriptElementLegacy[],
    trackId?: string,
  ): boolean {
    // This is a special operation that replaces the entire transcript,
    // so we don't use a command for it
    this.legacyTranscript = new LegacyTranscript(
      originalTranscriptElements as TranscriptElement[],
    );
    this.originalTranscript = new Transcript(originalTranscriptElements);
    let videoTrack: Track | undefined;
    if (!trackId) {
      // If no track ID is provided, use the first video track
      videoTrack = this.timeline.getVideoTrack();
      if (!videoTrack) {
        console.warn('No video track available to attach transcript');
        return false;
      }
      trackId = videoTrack.id!;
    } else {
      videoTrack = this.getTrackById(trackId);
      if (!videoTrack || videoTrack.type !== 'video') {
        console.warn(
          `Track with ID ${trackId} is not a valid video track for transcript`,
        );
        return false;
      }
    }

    const originalTokens = this.getOriginalTranscript()!;
    try {
      const transcriptTrack = TranscriptTrack.fromVideoTrackAndTranscript(
        videoTrack,
        transcriptElements,
        originalTokens,
      );

      const validationStartTime = performance.now();

      const validationResult = verifyTranscriptConsistencyQuick(
        transcriptElements,
        originalTokens,
        transcriptTrack,
      );

      const validationEndTime = performance.now();
      const validationDuration = validationEndTime - validationStartTime;
      console.info(
        `verifyTranscriptConsistency completed in ${validationDuration.toFixed(
          2,
        )}ms (status: ${validationResult.status})`,
      );
      if (this.flags.enableTranscriptMigrationValidation) {
        if (validationResult.status === 'error') {
          throw new TranscriptValidationError(
            'Transcript validation failed',
            validationResult.info,
          );
        }
      }

      // Replace the video track with the transcript track
      this.timeline.replaceTrack(trackId, transcriptTrack);
      console.debug('enableTranscriptSegmentArchitecture: transcript loaded');
    } catch (error) {
      if (error instanceof TranscriptValidationError) {
        throw error;
      }
      if (error instanceof Error) {
        console.warn(
          'Error creating transcript track, skipping load:',
          error.message,
        );
      } else {
        console.warn(
          'Unknown error creating transcript track, skipping load',
          error,
        );
      }
      return false;
    }

    // Store the track ID for future reference
    this.setTranscriptTrack(trackId);

    // Scenes are loaded before the transcript track exists; repair their
    // bindings now that the main track is known.
    this.normalizeSceneBindingsToTranscriptTrack(trackId);

    // Hydrate pending punch list items if any
    if (this.pendingPunchListItems && this.pendingPunchListItems.length > 0) {
      const fullTranscript = this.getFullTranscript();
      if (fullTranscript && fullTranscript.length > 0) {
        this.trackManager.hydratePunchListItems(
          fullTranscript,
          this.pendingPunchListItems,
        );
        this.pendingPunchListItems = null; // Clear after hydration
      }
    }

    // Notify observers about the transcript loading
    this.notifyObservers(EditorChangeType.TRANSCRIPT_LOADED, {
      transcript: this.originalTranscript,
    });

    return true;
  }

  /**
   * Restore the transcript track by rebuilding clip segments from the
   * original transcript. Executed as a command (undoable).
   */
  commandRestoreTranscriptFromOriginal(): boolean {
    return this.executeCommand(new RestoreTranscriptFromOriginalCommand(this));
  }

  /**
   * Load karaoke data into the editor
   * Creates a KaraokeTrack with the specified track number (KARAOKE_TRACK_NUMBER)
   * The track will be automatically serialized when Timeline.toRenderedJSON() / toStoredJSON() is called
   *
   * @param karaokeData - The karaoke data containing clips and configurations
   * @returns true if karaoke data was loaded successfully, false otherwise
   */
  loadKaraokeData(
    karaokeData: KaraokeData,
    opts?: { reconcileAgainstTranscript?: boolean },
  ): boolean {
    try {
      VideoEditorLogger.logKaraoke('load_karaoke_data_begin', {
        configsCount: karaokeData.configs.size,
        defaultConfigId: karaokeData.defaultConfigId,
        clipsCount: karaokeData.clips.length,
      });

      const existingVideoTranscriptTrack = this.getVideoTranscriptTrack();
      if (!existingVideoTranscriptTrack) {
        VideoEditorLogger.logKaraoke('load_karaoke_data_error', {
          error: 'No video transcript track set',
        });
        console.error(
          'Cannot load karaoke data: No video transcript track set',
        );
        return false;
      }
      const activeTranscriptTokens = this.getActiveTranscriptNew();

      // Check if a karaoke track already exists
      const existingKaraokeTrack = this.timeline.getTrack(
        String(KARAOKE_TRACK_NUMBER),
      );

      if (existingKaraokeTrack) {
        VideoEditorLogger.logKaraoke('load_karaoke_data_replace_existing', {
          existingTrackId: existingKaraokeTrack.id,
        });
        // Remove existing karaoke track
        this.timeline.removeTrack(String(KARAOKE_TRACK_NUMBER));
        console.debug('Removed existing karaoke track');
      }

      // PLAT-10788 — clone the incoming configs so the editor's live
      // `karaokeConfigs` never aliases the caller's KaraokeData. The per-language
      // cache (`KaraokeStore.loadedKaraokeDataByLanguage`) hands cached variants
      // straight to `loadKaraokeData`; without this a later in-place config
      // mutation (e.g. `applyCaptionsLanguage` switching the language via
      // `karaokeConfigs.set`) would rewrite the cached variant in place and
      // corrupt it. Symmetric with the clone `KaraokeStore.getKaraokeData` makes
      // on the way out.
      this.setKaraokeConfigs(cloneKaraokeConfigs(karaokeData.configs));
      this.defaultKaraokeConfigId = karaokeData.defaultConfigId;

      if (karaokeData.clips.length > 0) {
        // PLAT-11905 — the blob's top-level rows are always the source-timed
        // set; a dubbed language's dub-timed record travels beside them.
        const { dubRecord } = splitCaptionSets(
          karaokeData,
          this.getDefaultKaraokeConfig()?.language,
        );
        // Create KaraokeTrack from karaoke data using the static factory method
        const karaokeTrack = KaraokeTrack.fromKaraokeDataAndTranscript(
          karaokeData,
          activeTranscriptTokens,
        );
        karaokeTrack.dubRecord = dubRecord;
        // Add the karaoke track to the timeline
        this.timeline.addExistingTrack(karaokeTrack);

        // PLAT-10862 — a cached per-language variant is persisted at the point it
        // was last produced/edited; it does NOT follow later transcript edits made
        // while a DIFFERENT language was active (reconcile only runs on the live
        // track). So when a caller loads a variant that may be stale — a language
        // SWITCH or a PUBLISH build — reconcile it against the current transcript
        // here. Idempotent, so a fresh variant is untouched; guarded on a
        // non-empty transcript so an unexpectedly-empty one can't clear the
        // just-loaded track.
        if (opts?.reconcileAgainstTranscript && activeTranscriptTokens.length) {
          new KaraokeTrackManager(this).reconcileKaraokeTrack();
        }

        // Show the set the voice calls for. Showing the dub-timed set always
        // derives its captions, because their timing exists only in memory.
        this.syncCaptionSetToVoice();

        VideoEditorLogger.logKaraoke('load_karaoke_data_success', {
          trackId: KARAOKE_TRACK_NUMBER,
          clipsCount: karaokeTrack?.karaokeClips?.length,
          configsCount: karaokeData.configs.size,
        });

        console.debug(
          `Karaoke track created with ID ${KARAOKE_TRACK_NUMBER} and ${karaokeTrack.karaokeClips.length} clips`,
        );
      }

      // Notify observers about karaoke data loading
      this.notifyObservers(EditorChangeType.KARAOKE_LOADED, {
        karaokeData,
        trackId: String(KARAOKE_TRACK_NUMBER),
      });

      this.dirty = true;
      this.maybeSync();

      return true;
    } catch (error) {
      VideoEditorLogger.logKaraoke('load_karaoke_data_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      if (error instanceof Error) {
        console.error('Error loading karaoke data:', error.message);
      } else {
        console.error('Unknown error loading karaoke data', error);
      }
      return false;
    }
  }

  initializeDefaultKaraokeData(): boolean {
    VideoEditorLogger.logKaraoke('initialize_default_karaoke_data');
    if (this.getDefaultKaraokeConfig()) {
      return true; // prevent creating multiple default configs
    }
    const defaultKaraokeConfig = this.createKaraokeConfig(
      DEFAULT_EDITOR_KARAOKE_CONFIG,
    );
    this.defaultKaraokeConfigId = defaultKaraokeConfig.id;
    return true;
  }

  /**
   * Load scenes into the timeline
   *
   * @param scenes - Array of scenes configurations to restore
   * @returns true if scenes were loaded successfully, false otherwise
   */
  loadScenes(scenes: IScene[]): boolean {
    try {
      const sceneService = new SceneService();

      scenes.forEach(sceneData => {
        const scene = Scene.fromJSON(sceneData);
        sceneService.addScene(this.timeline, scene);
      });

      // After all scenes are loaded, remap scene bindings if tracks were renumbered during import
      this.timeline.adjustScenesAfterTrackRenumber();

      this.dirty = true;
      this.maybeSync();

      return true;
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error loading layout segments:', error.message);
      } else {
        console.error('Unknown error loading layout segments', error);
      }
      return false;
    }
  }

  /**
   * Creates a new track and adds it to the timeline
   * @param type The type of track to create
   * @returns The newly created track
   */
  commandCreateTrack(
    type: 'video' | 'audio' | 'text' | 'image' = 'video',
  ): Track {
    // Use command pattern for track creation
    const command = new CreateTrackCommand(this, type);
    this.executeCommand(command);

    // The command stores the created track, so we can access it
    return command.createdTrack!;
  }

  /**
   * Removes a track from the timeline
   * @param trackId The track or track ID to remove
   * @returns true if removed, false otherwise
   */
  commandRemoveTrack(
    trackId: Track | string,
    createUndoRedoEntry: boolean = true,
  ): Track | null | undefined {
    // Use command pattern for track removal
    const command = new RemoveTrackCommand(this, trackId, createUndoRedoEntry);
    const success = this.executeCommand(command);
    return success ? command.getRemovedTrack() : null;
  }

  /**
   * Adds an element to a track
   * @param trackId The ID of the track to add the element to
   * @param element The element to add
   * @param sync Whether to sync to the renderer immediately (default: true)
   */
  commandAddElementToTrack(trackId: string, element: Element): boolean {
    // Use command pattern for adding elements
    return this.executeCommand(new AddElementCommand(this, trackId, element));
  }

  /**
   * Creates a new element and adds it to a track
   */
  commandCreateAndAddElement(
    trackId: string,
    type: string,
    props: Record<string, any> = {},
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      props.time = this.timeline.snapTimeToFrame(props.time);
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    const element = Timeline.createElement(type, props);

    // Use command pattern for adding the element
    const success = this.executeCommand(
      new AddElementCommand(this, trackId, element),
    );

    return success ? element : null;
  }

  /**
   * Updates an element's properties
   * @param elementId The ID of the element to update
   * @param key The property key to update
   * @param value The new value for the property
   * @returns true if successful, false otherwise
   */
  commandUpdateElementProperty(
    elementId: string,
    key: string,
    value: any,
  ): boolean {
    const props: Record<string, any> = {};
    props[key] = value;

    // Use UpdateElementCommand for property updates
    return this.executeCommand(
      new UpdateElementCommand(this, elementId, props),
    );
  }

  /**
   * Updates multiple element properties at once
   * Can either accept a partial Element object or a state record from getState()
   */
  commandUpdateElement(
    elementId: string,
    props: Record<string, any>,
    createUndoRedoEntry: boolean = true,
  ): boolean {
    // Use factory method to handle potential type transitions
    return this.executeCommand(
      UpdateElementCommand.create(this, elementId, props, createUndoRedoEntry),
    );
  }

  commandUpdateMultipleElements(
    updates: Map<string, Record<string, Partial<Element>>>,
    createUndoEntry: boolean = true,
  ): boolean {
    return this.executeCommand(
      new UpdateMultipleElementsCommand(this, updates, createUndoEntry),
    );
  }

  /**
   * Updates timeline dimensions and multiple elements atomically.
   * Creates a single undo point for the entire operation.
   *
   * @param timelineUpdates - Timeline updates (width, height etc)
   * @param elementUpdates - Map of element IDs to their property updates
   * @param createUndoEntry - Whether to create an undo entry (defaults to true)
   * @returns true if successful, false otherwise
   */
  commandUpdateTimelineAndElements(
    timelineUpdates: TimelineUpdates,
    elementUpdates: Map<string, Partial<Element>>,
    createUndoEntry: boolean = true,
  ): boolean {
    return this.executeCommand(
      new UpdateTimelineAndElementsCommand(
        this,
        timelineUpdates,
        elementUpdates,
        createUndoEntry,
      ),
    );
  }

  /**
   * Removes an element from the timeline
   * @param elementId The ID of the element to remove
   * @param createUndoRedoEntry Whether to create an undo/redo entry
   * @param mode Timeline mode for ripple behavior:
   *   - DEFAULT: Just removes the element
   *   - RIPPLE: Shifts subsequent elements on the same track
   *   - RIPPLE_ALL_TRACKS: Shifts subsequent elements on ALL tracks
   */
  commandRemoveElement(
    elementId: string,
    createUndoRedoEntry: boolean = true,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ): boolean {
    // Use RemoveElementCommand for element removal
    return this.executeCommand(
      new RemoveElementCommand(this, elementId, createUndoRedoEntry, mode),
    );
  }

  /**
   * Sets an element's duration and shifts everything after it, on all tracks,
   * by the difference (PLAT-11732 — a blank media card taking its video's
   * length).
   */
  commandResizeElementRippleAllTracks(
    elementId: string,
    newDuration: number,
    createUndoRedoEntry: boolean = true,
  ): boolean {
    return this.executeCommand(
      new ResizeElementRippleAllTracksCommand(
        this,
        elementId,
        newDuration,
        createUndoRedoEntry,
      ),
    );
  }

  /**
   * Moves an element to a different track
   */
  commandMoveElementToTrack(elementId: string, targetTrackId: string): boolean {
    // Use MoveElementToTrackCommand for moving elements between tracks
    return this.executeCommand(
      new MoveElementToTrackCommand(this, elementId, targetTrackId),
    );
  }

  /**
   * Adds a media element to the timeline with collision detection and automatic track management.
   * Supports TimelineMode for different collision handling behaviors:
   * - DEFAULT: Creates new track on collision
   * - RIPPLE: Shifts subsequent elements to make room
   * - OVERLAP: Allows overlapping elements
   *
   * @param element The media element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns AddMediaElementCommand instance if successful, null otherwise
   */
  commandAddMediaElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    createUndoEntry: boolean = true,
  ): AddMediaElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddMediaElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      createUndoEntry,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Adds a music/audio element to the timeline with music-specific features:
   * - Fit to end: automatically adjusts duration to fill to timeline end
   * - Volume control: sets initial volume level
   * - Auto fade: applies fade in/out effects
   * - Smart track placement: fills gaps in existing music tracks before creating new ones
   *
   * @param element The music/audio element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @param options Music-specific options (fitToEnd, volume, autoFade)
   * @returns AddMusicElementCommand instance if successful, null otherwise
   */
  commandAddMusicElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    options?: AddMusicElementOptions,
    createUndoEntry: boolean = true,
  ): AddMusicElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddMusicElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      options,
      createUndoEntry,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Creates and adds a text element to the timeline with smart positioning and validation
   * @param props Text element properties and configuration
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns The created Element if successful, null otherwise
   */
  commandCreateTextElement(
    props: Record<string, any> = {},
    targetTrackId?: string,
    mode?: TimelineMode,
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      // This is to account for the fact that the snapTimeToFrame function
      // rounds up to the nearest frame and the time we get here can be in
      // between frames.
      //
      // Ideally we should always snap the timeline playhead to the nearest
      // frame so time values are always indexed at the start of a frame
      // but this is a quick fix.
      const shiftedTime = props.time - 1 / this.timeline.framerate;
      props.time = this.timeline.snapTimeToFrame(
        shiftedTime < 0 ? 0 : shiftedTime,
      );
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    // Set default text properties if not provided
    const textProps = {
      type: 'text',
      duration: 4,
      font_size: '32',
      font_family: 'Inter',
      font_weight: '600',
      fill_color: 'rgba(0, 0, 0, 1)',
      background_color: 'rgba(255, 255, 255, 0.7)',
      background_align_threshold: '0%',
      background_border_radius: '0%',
      width: '76%',
      y: '60%',
      ...props,
    };

    const element = Timeline.createElement('text', textProps);

    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddTextElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? element : null;
  }

  /**
   * Creates and adds a shape element to the timeline with smart positioning and validation
   * @param props Shape element properties and configuration
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns The created Element if successful, null otherwise
   */
  commandCreateShapeElement(
    props: Record<string, any> = {},
    targetTrackId?: string,
    mode?: TimelineMode,
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      const shiftedTime = props.time - 1 / this.timeline.framerate;
      props.time = this.timeline.snapTimeToFrame(
        shiftedTime < 0 ? 0 : shiftedTime,
      );
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    // Set default text properties if not provided
    const shapeProps = {
      type: 'shape',
      x: '25%',
      y: '25%',
      duration: 4,
      width: '50%',
      height: '50%',
      x_anchor: '50%',
      y_anchor: '50%',
      path: 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z',
      fill_color: '#575757',
      ...props,
    };

    const element = Timeline.createElement('shape', shapeProps);

    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddShapeElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? element : null;
  }

  /**
   * Creates and adds a shape element to the timeline with smart positioning and validation
   * @param props Shape element properties and configuration
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @param rippleConfig Config for transition overlap adjustment during ripple
   * @returns The created Element if successful, null otherwise
   */
  commandCreateCompositionElement(
    props: Record<string, any> = {},
    targetTrackId?: string,
    timelineMode?: TimelineMode,
    createUndoEntry: boolean = true,
    rippleConfig?: RippleConfig,
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (typeof props.time === 'number') {
      const shiftedTime = props.time - 1 / this.timeline.framerate;
      props.time = this.timeline.snapTimeToFrame(
        shiftedTime < 0 ? 0 : shiftedTime,
      );
    }

    if (typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    const element = Timeline.createElement('composition', props);

    const effectiveMode = timelineMode ?? this.getTimelineMode();
    const command = new AddCompositionElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      createUndoEntry,
      rippleConfig,
    );

    const success = this.executeCommand(command);
    return success ? element : null;
  }

  /**
   * Adds a video element to the timeline with video-specific features:
   * - Volume control: sets initial volume level
   * - Video fit: controls how video is resized to fit the element
   * - Loop control: enables/disables video looping
   * - Auto fade: applies fade in/out effects to video audio
   * - Smart track placement: prefers video tracks and handles collision detection
   *
   * @param element The video element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @param options Video-specific options (volume, fit, loop, autoFade)
   * @returns AddVideoElementCommand instance if successful, null otherwise
   */
  commandAddVideoElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    options?: AddVideoElementOptions,
    createUndoEntry: boolean = true,
  ): AddVideoElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddVideoElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      options,
      createUndoEntry,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Adds a scene to the timeline
   */
  commandAddScene(scene: Scene): boolean {
    const command = new AddSceneCommand(this, scene);
    return this.executeCommand(command);
  }

  /**
   * Updates a scene's properties
   */
  commandUpdateScene(sceneId: string, updates: Partial<IScene>): boolean {
    const command = new UpdateSceneCommand(this, sceneId, updates);
    return this.executeCommand(command);
  }

  commandUpdateScenes(
    updates: Array<{ sceneId: string; updates: Partial<IScene> }>,
  ): boolean {
    if (updates.length === 0) {
      return true;
    }

    const command = new UpdateScenesCommand(this, updates);
    return this.executeCommand(command);
  }

  /**
   * Creates scenes based on media time detections
   * Finds elements on the transcript track that contain or overlap with scene times
   * and anchors scenes to those elements with proper relative times
   */
  commandCreateScenesByMediaTime(
    sceneDetections: SceneDetection[],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
  ): boolean {
    const command = new CreateScenesByMediaTimeCommand(
      this,
      sceneDetections,
      naturalMediaDimensions,
    );
    return this.executeCommand(command);
  }

  commandRemoveScene(sceneId: string): boolean {
    const command = new RemoveSceneCommand(this, sceneId);
    return this.executeCommand(command);
  }

  commandSplitScene(sceneId: string, relativeTime: number): boolean {
    const command = new SplitSceneCommand(this, sceneId, relativeTime);
    return this.executeCommand(command);
  }

  /**
   * Gets a timeline element by ID
   */
  getElementById(elementId: string): Element | undefined {
    // find element by id or find nested element in composition
    for (const element of this.timeline.allElements) {
      if (element.id === elementId) {
        return element;
      }
      if (Element.isCompositionElement(element)) {
        const foundNestedElement = element.findElement(
          el => el.id === elementId,
        );
        if (foundNestedElement) {
          return foundNestedElement;
        }
      }
    }
  }

  /**
   * Returns the top-level timeline element whose subtree contains `elementId`
   * — e.g. the composition that owns an inner/graphics element. Returns the
   * element itself when it is already top-level, and undefined when the id is
   * not a timeline element (e.g. a synthetic scene representation), letting
   * callers fall back to the element itself.
   */
  getTopLevelElementContaining(elementId: string): Element | undefined {
    return this.timeline.allElements.find(
      element =>
        element.id === elementId ||
        (Element.isCompositionElement(element) &&
          !!element.findElement(el => el.id === elementId)),
    );
  }

  /**
   * Gets a track by ID
   */
  getTrackById(trackId: string): Track | undefined {
    return this.timeline.tracks.find(track => track.id === trackId);
  }

  // Timeline Element Operations with Command Pattern

  /**
   * Moves an element to a new time position
   * Uses command pattern and delegates to TrackManager via MoveElementCommand
   * @param elementId The ID of the element to move
   * @param newStartTime The new start time for the element (in seconds)
   * @param snapToFrame Whether to snap the time to frame boundaries
   * @returns true if the operation was successful, false otherwise
   */
  commandShiftElement(
    elementId: string,
    newStartTime: number,
    newTrack?: number,
    snapToFrame: boolean = false,
  ): boolean {
    // Resolve element and track if IDs were provided
    const element = this.getElementById(elementId);
    if (!element) return false;

    // Execute the move command with the resolved objects
    return this.executeCommand(
      new ShiftElementCommand(
        this,
        element,
        newStartTime,
        newTrack,
        snapToFrame,
      ),
    );
  }

  commandShiftMultipleElements(
    elementIds: string[],
    timeShift: number,
    snapToFrame: boolean = false,
    shouldCreateUndoRedoEntry: boolean = true,
  ): boolean {
    return this.executeCommand(
      new ShiftMultipleElementsCommand(
        this,
        elementIds,
        timeShift,
        snapToFrame,
        shouldCreateUndoRedoEntry,
      ),
    );
  }

  commandChangeElementPlacements(
    placements: Array<{
      elementId: string;
      newStartTime: number;
    }>,
  ): boolean {
    const elementPlacements = placements.map(placement => ({
      elementId: placement.elementId,
      element: this.getElementById(placement.elementId),
      time: placement.newStartTime,
    }));

    if (elementPlacements.some(elPlacement => !elPlacement.element)) {
      VideoEditorLogger.logTimelineOperation(
        'change_placements_elements_not_found',
        {
          error: 'Elements not found to apply placements',
          placements: elementPlacements.filter(
            elPlacement => !elPlacement.element,
          ),
        },
      );
      return false;
    }
    return this.executeCommand(
      new ChangeElementPlacementsCommand(
        this,
        elementPlacements as ElementPlacement<Element>[],
      ),
    );
  }

  // commandShiftMultipleElementsWithDifferentOffsets(
  //   placements: { element: ElementState; placement: { globalTime: number } }[],
  // ): boolean {
  //   return this.executeCommand(
  //     new ShiftMultipleElementsWithDifferentOffsetsCommand(this, placements),
  //   );
  // }

  /**
   * Trims the start of an element (adjusts start time and duration)
   * Uses command pattern and delegates to TrackManager via TrimElementStartCommand
   * @param elementId The element or ID of the element to trim
   * @param newStartTime The new start time for the element (in seconds)
   * @param snapToFrame Whether to snap the time to frame boundaries (default: false)
   * @param sceneDetections Optional scene detections for creating proper scenes when extending
   * @param naturalMediaDimensions Optional natural dimensions for computing crop
   * @param faceDetections Optional face detections for smart framing
   * @param framingService Optional framing service for calculating per-scene crops
   * @returns true if the operation was successful, false otherwise
   */
  commandTrimElementStart(
    elementId: string,
    newStartTime: number,
    snapToFrame: boolean = false,
    sceneDetections?: SceneDetection[],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    faceDetections?: FaceDetection[],
    framingService?: ISceneFramingService,
  ): boolean {
    // Resolve element if an ID was provided
    const element = this.getElementById(elementId);

    if (!element) return false;

    return this.executeCommand(
      new TrimElementStartCommand(
        this,
        element,
        newStartTime,
        snapToFrame,
        sceneDetections,
        naturalMediaDimensions,
        faceDetections,
        framingService,
      ),
    );
  }

  /**
   * Trims the end of an element (adjusts duration)
   * Uses command pattern and delegates to TrackManager via TrimElementEndCommand
   * @param elementId The element or ID of the element to trim
   * @param newEndTime The new end time for the element (in seconds)
   * @param snapToFrame Whether to snap the time to frame boundaries (default: false)
   * @param sceneDetections Optional scene detections for creating proper scenes when extending
   * @param naturalMediaDimensions Optional natural dimensions for computing crop
   * @param faceDetections Optional face detections for smart framing
   * @param framingService Optional framing service for calculating per-scene crops
   * @returns true if the operation was successful, false otherwise
   */
  commandTrimElementEnd(
    elementId: string,
    newEndTime: number,
    snapToFrame: boolean = false,
    sceneDetections?: SceneDetection[],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    faceDetections?: FaceDetection[],
    framingService?: ISceneFramingService,
  ): boolean {
    // Resolve element if an ID was provided
    const element = this.getElementById(elementId);

    if (!element) return false;

    return this.executeCommand(
      new TrimElementEndCommand(
        this,
        element,
        newEndTime,
        snapToFrame,
        sceneDetections,
        naturalMediaDimensions,
        faceDetections,
        framingService,
      ),
    );
  }

  /**
   * Splits an element at the specified time
   * @param elementId The ID of the element to split
   * @param splitTime The time (in seconds) where the split should occur (playhead position)
   * @returns An array with the two resulting elements, or null if the operation failed
   */
  commandSplitElement(
    elementId: string,
    splitTime: number,
  ): [Element, Element] | null {
    const element = this.getElementById(elementId);
    if (!element) return null;

    // Validate that split time is within element bounds
    if (
      splitTime <= element.time ||
      splitTime >= element.time + element.durationSeconds
    ) {
      VideoEditorLogger.logTimelineOperation('split_element_error', {
        error: 'Split time must be within element bounds',
        element: element.id,
        elementType: element.type,
        elementTime: element.time,
        elementDuration: element.duration,
        elementDurationSeconds: element.durationSeconds,
        elementEnd: element.time + element.durationSeconds,
        requestedSplitTime: splitTime,
      });
      return null;
    }

    const command = new SplitElementCommand(this, element, splitTime);
    const success = this.executeCommand(command);

    return success ? command.getResultElements() : null;
  }

  /**
   * Shifts a track up in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  commandShiftTrackLayerUp(trackId: string): boolean {
    return this.executeCommand(new TrackLayerUpCommand(this, trackId));
  }

  /**
   * Shifts a track down in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  commandShiftTrackLayerDown(trackId: string): boolean {
    return this.executeCommand(new TrackLayerDownCommand(this, trackId));
  }

  /**
   * Sets the timeline duration
   * @param duration The new duration in seconds, or null to auto-calculate from elements
   * @returns true if successful, false otherwise
   */
  commandSetTimelineDuration(
    duration: number | null,
    shouldCreateUndoRedoEntry: boolean = true,
  ): boolean {
    return this.executeCommand(
      new SetTimelineDurationCommand(this, duration, shouldCreateUndoRedoEntry),
    );
  }

  /**
   * Crops the timeline to the inclusive range [keepStart, keepEnd]. Elements
   * entirely outside are removed; partially overlapping are trimmed and
   * shifted.
   * Wrapper around CropTimelineCommand.
   * @param keepStart start time in seconds (will be clamped to >=0)
   * @param keepEnd end time in seconds (if < keepStart it will be normalized)
   * @param trackIdsToSkip optional array of track IDs to skip cropping (e.g. tracks that were cropped already - i.e. original video track that was cropped by transcript remove command)
   * @param sceneSnapThresholdFrames optional threshold in frames for snapping crop points to scene boundaries
   * @returns true if successful, false otherwise
   */
  commandCropTimeline(
    ranges: { start: number; end: number }[],
    trackIdsToSkip: string[] = [],
    sceneSnapThresholdFrames: number = DEFAULT_SCENE_SNAP_THRESHOLD_FRAMES,
  ): boolean {
    return this.executeCommand(
      new CropTimelineCommand(
        this,
        ranges,
        trackIdsToSkip,
        sceneSnapThresholdFrames,
      ),
    );
  }

  /**
   * Reel-specific crop. Keeps only the given ranges (in order), emitting the
   * kept portions of the video-transcript track as plain video elements, then
   * rebuilds the transcript track from the original transcript and the scenes
   * track from media-time detections.
   *
   * Only supported when the video-transcript track is the sole track on the
   * timeline; otherwise no changes are made and this returns false (other
   * tracks are not cropped and would be left misaligned).
   * Wrapper around CropTimelineToReelCommand.
   * @returns true if successful, false otherwise
   */
  commandCropTimelineToReel(
    ranges: { start: number; end: number }[],
    sceneDetections: SceneDetection[] = [],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    sceneSnapThresholdFrames: number = DEFAULT_SCENE_SNAP_THRESHOLD_FRAMES,
  ): boolean {
    return this.executeCommand(
      new CropTimelineToReelCommand(
        this,
        ranges,
        sceneDetections,
        naturalMediaDimensions,
        sceneSnapThresholdFrames,
      ),
    );
  }

  /**
   * PLAT-11053 — moves a reel Section to a new position in playback order,
   * carrying the elements it owns. One undo entry per move.
   *
   * @param sectionId Provenance id of the Section to move
   * @param targetIndex Its new position in playback order, from 0
   * @param exemptElementIds Reel-level elements that must not be carried (DE7)
   * @returns false when the Section is unknown or already at that index
   */
  commandMoveReelSection(
    sectionId: string,
    targetIndex: number,
    exemptElementIds?: ReadonlySet<string>,
  ): boolean {
    return this.executeCommand(
      new MoveReelSectionCommand(
        this,
        sectionId,
        targetIndex,
        exemptElementIds,
      ),
    );
  }

  /**
   * PLAT-11071 — swaps a Section's passage for a different clip in place: same
   * position in the running order, same chapter, one undo entry.
   *
   * @param sectionId Provenance id of the Section to replace
   * @param ranges The incoming clip's source media ranges — one for an ordinary
   *   clip, one per passage for a clip that is itself a reel (DE15)
   * @param exemptElementIds Reel-level elements that must not be carried (DE7)
   * @returns The replacement Sections' provenance ids, or null when nothing changed
   */
  commandReplaceReelSection(
    sectionId: string,
    ranges: { start: number; end: number }[],
    exemptElementIds?: ReadonlySet<string>,
  ): string[] | null {
    const command = new ReplaceReelSectionCommand(
      this,
      sectionId,
      ranges,
      exemptElementIds,
    );
    return this.executeCommand(command) ? command.addedSectionIds : null;
  }

  /**
   * PLAT-11060 — drops a reel Section, together with the elements it owns.
   * Removing is not deleting the source clip (DS8). One undo entry.
   *
   * @param sectionId Provenance id of the Section to remove
   * @param exemptElementIds Reel-level elements that must not be removed (DE7)
   * @returns false when the Section is unknown or is the reel's last one (DS10)
   */
  commandRemoveReelSection(
    sectionId: string,
    exemptElementIds?: ReadonlySet<string>,
  ): boolean {
    return this.executeCommand(
      new RemoveReelSectionCommand(this, sectionId, exemptElementIds),
    );
  }

  /**
   * PLAT-11056 — moves a whole Chapter to a new position in playback order,
   * carrying every element its Sections own. One undo entry for the move.
   *
   * @param sectionIds The Chapter's Sections, in playback order
   * @param targetIndex Where the run is re-inserted, in the order after it has
   *   been lifted out
   * @param exemptElementIds Reel-level elements that must not be carried (DE7)
   * @returns false when the run is unknown, not contiguous, or already there
   */
  commandMoveReelChapter(
    sectionIds: readonly string[],
    targetIndex: number,
    exemptElementIds?: ReadonlySet<string>,
  ): boolean {
    return this.executeCommand(
      new MoveReelChapterCommand(
        this,
        sectionIds,
        targetIndex,
        exemptElementIds,
      ),
    );
  }

  /**
   * PLAT-11153 — puts the reel's Sections in a new order, carrying every
   * element each one owns. One undo entry for the whole rearrangement.
   *
   * @param orderedSectionIds Every live Section, in the order it should play
   * @param exemptElementIds Reel-level elements that must not be carried (DE7)
   * @returns false when the list is not a permutation of the reel, or changes
   *   nothing
   */
  commandReorderReelSections(
    orderedSectionIds: readonly string[],
    exemptElementIds?: ReadonlySet<string>,
  ): boolean {
    return this.executeCommand(
      new ReorderReelSectionsCommand(this, orderedSectionIds, exemptElementIds),
    );
  }

  /**
   * PLAT-11057 — adds source ranges to an existing reel as new Sections, at a
   * Section boundary. One undo entry for the whole multi-clip add.
   *
   * @param ranges Source media ranges, in the order they should play
   * @param atIndex Boundary to insert before: 0 opens the reel, `sections.length` ends it
   * @param exemptElementIds Reel-level elements that must not be carried (DE7)
   * @returns The new Sections' provenance ids, or null when nothing was added
   */
  commandInsertReelSections(
    ranges: { start: number; end: number }[],
    atIndex: number,
    exemptElementIds?: ReadonlySet<string>,
  ): string[] | null {
    const command = new InsertReelSectionsCommand(
      this,
      ranges,
      atIndex,
      exemptElementIds,
    );
    return this.executeCommand(command) ? command.insertedSectionIds : null;
  }

  /**
   * Triggers auto-calculation of timeline duration based on elements
   * This sets the duration to null, which causes Creatomate to calculate the duration
   * based on the furthest element end time
   * @returns true if successful, false otherwise
   */
  commandAutoCalculateTimelineDuration(): boolean {
    return this.executeCommand(new SetTimelineDurationCommand(this, null));
  }

  commandRemoveTranscript(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    toClipboard: boolean = false,
    sceneSnapThresholdFrames: number = DEFAULT_SCENE_SNAP_THRESHOLD_FRAMES,
  ): boolean {
    const commandsExecuted: (TranscriptRemoveCommand | RemoveElementCommand)[] =
      [];

    const success = this.executeInBatch(() => {
      let success = true;

      // Get transcript track to access segments in order
      const transcriptTrack = this.getTranscriptTrack();
      if (!transcriptTrack || !(transcriptTrack instanceof TranscriptTrack)) {
        VideoEditorLogger.logTranscriptOperation('segment_remove_error', {
          error: 'no_transcript_track',
        });
        return false;
      }

      // Get all transcript segment elements in timeline order
      const allSegmentElements = transcriptTrack.transcriptSegmentElements;

      // Find the segments to process (from fromSegmentId to toSegmentId)
      const fromSegmentIndex = allSegmentElements.findIndex(
        seg => seg.getClipTranscriptSegment().id === fromSegmentId,
      );
      const toSegmentIndex = allSegmentElements.findIndex(
        seg => seg.getClipTranscriptSegment().id === toSegmentId,
      );

      if (fromSegmentIndex === -1 || toSegmentIndex === -1) {
        VideoEditorLogger.logTranscriptOperation('segment_remove_error', {
          error: 'segment_not_found',
          fromSegmentId,
          toSegmentId,
        });
        return false;
      }

      // Ensure we process segments in the correct order
      const startIndex = Math.min(fromSegmentIndex, toSegmentIndex);
      const endIndex = Math.max(fromSegmentIndex, toSegmentIndex);
      const segmentsToProcess = allSegmentElements.slice(
        startIndex,
        endIndex + 1,
      );

      // Process each segment with appropriate trimHead/trimTail logic
      for (let i = 0; i < segmentsToProcess.length; i++) {
        const segmentElement = segmentsToProcess[i];

        // Skip segments that are already removed (RemovedTranscriptClip instances)
        if (!(segmentElement instanceof VideoTranscriptElement)) {
          continue;
        }

        // Get active elements to ensure this segment has content to remove
        const segmentWordsWithPunctuation = segmentElement
          .getClipTranscriptSegment()
          .getWordsWithPunctuation();
        if (segmentWordsWithPunctuation.length === 0) {
          // todo: delete element completely if it is in the middle and doesn't contain words

          const command = new RemoveElementCommand(
            this,
            (segmentElement as VideoTranscriptElement).id,
            commandsExecuted.length === 0,
          );

          const result = this.executeCommand(command);
          commandsExecuted.push(command);
          success = success && result;
          continue;
        }

        // Determine trim parameters:
        // - trimHead: true for all segments except the first
        // - trimTail: true for all segments except the last
        const isFirstSegment = i === 0;
        const isLastSegment = i === segmentsToProcess.length - 1;
        const coversMediaIntro =
          isFirstSegment &&
          segmentElement.getMediaStartTime() === 0 &&
          segmentElement.getClipTranscriptSegment().firstWord &&
          segmentElement.getClipTranscriptSegment().firstWord!.relative_ts >
            this.config.maxMediaDurationToTrim;
        const coversMediaOutro =
          isLastSegment &&
          Math.abs(
            segmentElement.getMediaEndTime() - segmentElement.media_duration!,
          ) <
            1 / this.getFramerate() &&
          segmentElement.getClipTranscriptSegment().lastWord &&
          segmentElement.getClipTranscriptSegment().lastWord!.relative_end_ts <
            segmentElement.durationSeconds - this.config.maxMediaDurationToTrim;
        const trimHead = !coversMediaIntro;
        const trimTail = !coversMediaOutro;

        // Determine the range for this segment
        let segmentFromIndex: number;
        let segmentToIndex: number;
        const segmentId = segmentElement.getClipTranscriptSegment().id;

        if (segmentId === fromSegmentId) {
          // First segment: use provided fromIndex
          segmentFromIndex = fromIndex;
          segmentToIndex =
            segmentId === toSegmentId
              ? toIndex
              : segmentWordsWithPunctuation.length - 1;
        } else if (segmentId === toSegmentId) {
          // Last segment: use provided toIndex
          segmentFromIndex = 0;
          segmentToIndex = toIndex;
        } else {
          // Middle segment: remove all words
          segmentFromIndex = 0;
          segmentToIndex = segmentWordsWithPunctuation.length - 1;
        }

        // Create and execute the command for this segment.
        // PLAT-11387 — one Remove Text is one user action, so only the first
        // segment's command carries the undo entry: a host snapshots before the
        // command it is told about, and that has to be the state before the
        // whole operation, not before each segment in it.
        const command = new TranscriptRemoveCommand(
          this,
          segmentId,
          segmentFromIndex,
          segmentToIndex,
          trimHead,
          trimTail,
          sceneSnapThresholdFrames,
          commandsExecuted.length === 0,
        );

        const result = this.executeCommand(command);
        commandsExecuted.push(command);
        // Track overall success - only successful if all segments are processed
        success = success && result;
      }

      return success;
    });

    if (toClipboard && success) {
      this.cutPasteClipboardEntries = commandsExecuted
        .filter(
          (cmd): cmd is TranscriptRemoveCommand =>
            cmd instanceof TranscriptRemoveCommand,
        )
        .map(cmd => cmd.getOperationResult()?.clipboardEntry)
        .filter((entry): entry is TranscriptClipboardEntry => entry != null);
      console.log('Clipboard', this.cutPasteClipboardEntries);
    }

    return success;
  }

  /**
   * Paste segments from clipboard to a specific position in the transcript
   * @param clipSegmentId The ID of the segment to paste before/after
   * @param clipIndex The index within the segment (relative to wordsWithPunctuation)
   * @param after Whether to paste after (true) or before (false) the specified position
   * @returns true if paste was successful, false otherwise
   */
  commandPasteTranscript(
    clipSegmentId: string,
    clipIndex: number,
    after: boolean,
  ): boolean {
    // Check if we have clipboard segments to paste
    const clipboardEntries = this.cutPasteClipboardEntries;
    if (!clipboardEntries || clipboardEntries.length === 0) {
      VideoEditorLogger.logTranscriptOperation('segment_paste_error', {
        error: 'empty_clipboard',
      });
      return false;
    }

    // Get transcript track to access segments
    const transcriptTrack = this.getTranscriptTrack();
    if (!transcriptTrack || !(transcriptTrack instanceof TranscriptTrack)) {
      VideoEditorLogger.logTranscriptOperation('segment_paste_error', {
        error: 'no_transcript_track',
      });
      return false;
    }

    // Create and execute the paste command
    const command = new TranscriptPasteCommand(
      this,
      clipboardEntries,
      clipSegmentId,
      clipIndex,
      after,
    );

    const success = this.executeCommand(command);

    if (success) {
      VideoEditorLogger.logTranscriptOperation('segment_paste_success', {
        clipSegmentId,
        clipIndex,
        after,
        segmentsCount: clipboardEntries.length,
      });
      this.cutPasteClipboardEntries = null; // Clear clipboard after pasting
    } else {
      VideoEditorLogger.logTranscriptOperation('segment_paste_error', {
        error: 'command_execution_failed',
        clipSegmentId,
        clipIndex,
        after,
      });
    }

    return success;
  }

  /**
   * Gets the current clipboard segments from cut operations
   * @returns Readonly array of clipboard segments, or empty array if none available
   */
  getClipboardEntries(): TranscriptClipboardEntry[] {
    return this.cutPasteClipboardEntries || [];
  }

  /**
   * Stores a timeline element snapshot in the clipboard for paste.
   */
  setElementClipboard(entry: ElementClipboardEntry | null): void {
    this._elementClipboard = entry;
  }

  /**
   * Returns the current element clipboard entry, or null if empty.
   */
  getElementClipboard(): ElementClipboardEntry | null {
    return this._elementClipboard;
  }

  commandTranscriptRestore(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    sceneDetections?: SceneDetection[],
    faceDetections?: FaceDetection[],
    framingService?: ISceneFramingService,
    sceneSnapThresholdFrames: number = DEFAULT_SCENE_SNAP_THRESHOLD_FRAMES,
  ): boolean {
    if (!this.originalTranscript) {
      VideoEditorLogger.logTranscriptOperation('segment_restore_error', {
        error: 'no_transcript',
      });
      return false;
    }

    // Handle multi-segment restoration
    if (fromSegmentId !== toSegmentId) {
      // Get all transcript track elements
      const transcriptTrack = this.getVideoTranscriptTrack();
      if (!transcriptTrack) {
        VideoEditorLogger.logTranscriptOperation('segment_restore_error', {
          error: 'no_transcript_track',
        });
        return false;
      }

      const allElements = transcriptTrack.transcriptSegmentElements;
      const fromElementIndex = allElements.findIndex(
        el => el.getClipTranscriptSegment().id === fromSegmentId,
      );
      const toElementIndex = allElements.findIndex(
        el => el.getClipTranscriptSegment().id === toSegmentId,
      );

      if (fromElementIndex === -1 || toElementIndex === -1) {
        VideoEditorLogger.logTranscriptOperation('segment_restore_error', {
          error: 'invalid_segment_ids',
          fromSegmentId,
          toSegmentId,
        });
        return false;
      }

      // Execute restore commands for each segment in the range
      return this.executeInBatch(() => {
        let success = true;

        for (let i = fromElementIndex; i <= toElementIndex; i++) {
          const element = allElements[i];
          const segmentId = element.getClipTranscriptSegment().id;
          const wordsWithPunctuation = element
            .getClipTranscriptSegment()
            .getWordsWithPunctuation();

          // Determine indices for this segment
          const startIdx = i === fromElementIndex ? fromIndex : 0;
          const endIdx =
            i === toElementIndex ? toIndex : wordsWithPunctuation.length - 1;

          const transcriptRestoreCommand = new TranscriptRestoreCommand(
            this,
            segmentId,
            startIdx,
            segmentId,
            endIdx,
            naturalMediaDimensions,
            sceneDetections,
            faceDetections,
            framingService,
            sceneSnapThresholdFrames,
          );

          const result = this.executeCommand(transcriptRestoreCommand);
          success = success && result;
        }

        return success;
      });
    }

    // Single segment restoration
    const transcriptRestoreCommand = new TranscriptRestoreCommand(
      this,
      fromSegmentId,
      fromIndex,
      toSegmentId,
      toIndex,
      naturalMediaDimensions,
      sceneDetections,
      faceDetections,
      framingService,
      sceneSnapThresholdFrames,
    );

    const success = this.executeCommand(transcriptRestoreCommand);
    return success;
  }

  commandChangeAspectRatio(
    originalVideoDimensions: VideoDimensions,
    prevVideoDimensions: VideoDimensions,
    newVideoDimensions: VideoDimensions,
    naturalMediaDimensions: Record<string, NaturalDimensions>,
    basicTextSetting: Record<'font_size' | 'y' | 'width', string>,
    basicKaraokeSetting: Record<'font_size' | 'y' | 'width', string>,
    karaokePresetProvider?: IKaraokePresetProvider,
    variantService?: IKaraokeTemplateVariantService,
  ): boolean {
    return this.executeCommand(
      new ChangeAspectRatioCommand(this, {
        originalVideoDimensions,
        prevVideoDimensions,
        newVideoDimensions,
        naturalMediaDimensions,
        basicTextSetting,
        basicKaraokeSetting,
        karaokePresetProvider,
        variantService,
      }),
    );
  }

  /**
   * Gets the original, immutable transcript
   * @returns The original transcript elements, or undefined if no transcript is loaded
   */
  getOriginalTranscript(): ReadonlyArray<TranscriptElementLegacy> | undefined {
    if (!this.originalTranscript) return undefined;
    return this.originalTranscript.getOriginalTranscription();
  }

  /**
   * Gets the active transcript based on video elements from the transcript track
   * @returns The active transcript elements with modifications applied and timeline timestamps
   */
  getActiveTranscript(): TranscriptElementLegacy[] | undefined {
    return this.getActiveTranscriptNew();
  }

  getActiveTranscriptNew() {
    let videoElements: readonly VideoTranscriptElement[] = [];

    const mainVideoTrack = this.getVideoTranscriptTrack();
    if (mainVideoTrack && mainVideoTrack instanceof TranscriptTrack) {
      // only existing video elements
      videoElements = mainVideoTrack.elements;
      console.log(
        `Using ${videoElements.length} video elements from transcript track ${mainVideoTrack.id}`,
      );
    }

    const activeTranscript = videoElements.flatMap(ve =>
      ve.getActiveTranscriptElements(),
    );
    return activeTranscript;
  }

  /**
   * Gets the full transcript based on transcript segment elements from the transcript track
   * @returns The full transcript elements with modifications applied and timeline timestamps
   */
  getFullTranscript(): ActiveTranscriptTokenWithClipSegmentAttributes[] {
    if (!this.originalTranscript) return [];

    let transcriptSegments: readonly HasClipTranscriptSegment[] = [];

    const mainVideoTrack = this.getVideoTranscriptTrack();
    if (mainVideoTrack && mainVideoTrack instanceof TranscriptTrack) {
      // all segments (video + removed clips)
      transcriptSegments = mainVideoTrack.transcriptSegmentElements;
      console.log(
        `Using ${transcriptSegments.length} TranscriptSegments elements from transcript track ${mainVideoTrack.id}`,
      );
    }

    // Get active transcript using the Transcript class's existing implementation
    const activeTranscript = transcriptSegments.flatMap(segment =>
      segment.getActiveTranscriptElements(),
    );

    return fillInWhitespacesInTranscriptElements(activeTranscript);
  }

  /**
   * Get karaoke configurations as a Map
   * @returns Map of karaoke config ID to KaraokeConfig
   */
  getKaraokeConfigsMap(): Map<string, KaraokeConfig> {
    return this.karaokeConfigs;
  }

  /**
   * Set karaoke configurations
   * @param configs - Map of karaoke config ID to KaraokeConfig
   */
  setKaraokeConfigs(configs: Map<string, KaraokeConfig>): void {
    VideoEditorLogger.logKaraoke('set_karaoke_configs', {
      configsCount: configs.size,
      configIds: Array.from(configs.keys()),
    });
    this.karaokeConfigs = configs;
  }

  /**
   * Set the default karaoke config ID
   * @param configId - ID of the default karaoke config
   */
  setDefaultKaraokeConfigId(configId: string): void {
    VideoEditorLogger.logKaraoke('set_default_karaoke_config_id', {
      configId,
    });
    this.defaultKaraokeConfigId = configId;
  }

  /**
   * Get karaoke configurations (alias for getKaraokeConfigsMap)
   * @returns Map of karaoke config ID to KaraokeConfig
   */
  getKaraokeConfigs(): Map<string, KaraokeConfig> {
    return this.karaokeConfigs;
  }

  /**
   * Get karaoke lines from the karaoke track
   * @returns Array of karaoke lines, or empty array if no karaoke track exists
   */
  getKaraokeLines(): KaraokeLine[] {
    const karaokeTrack = this.getKaraokeTrack();
    if (!karaokeTrack) {
      return [];
    }
    const lines = karaokeTrack.karaokeClips.map(clip => clip.toKaraokeLine());
    return lines;
  }

  /**
   * PLAT-11905 — the caption track as the editor's exports write it (the Clip
   * Preview copy for a new aspect ratio and its undo snapshot, and
   * `exportVideoSources`): the source-timed rows on top, whichever set is
   * shown, and a dubbed language's dub-timed record beside them. As on
   * origin/dev, these exports carry no break records; the caption store's save
   * (`KaraokeStore.getKaraokeData`) does. The dub-timed set's own manual breaks
   * live in its record. `null` when there are no captions.
   */
  getCaptionData(): KaraokeData | null {
    const track = this.getKaraokeTrack();
    if (
      !track ||
      track.karaokeClips.length + track.sourceSet.clips.length === 0
    ) {
      return null;
    }
    const parked = track.sourceParked;
    const { clips, dubTimed } = composeCaptionSets({
      liveKind: track.liveKind,
      liveLines: this.getKaraokeLines(),
      liveBreaks: track.syntheticBreaks,
      parkedSourceLines: parked
        ? parked.clips.map(clip => clip.toKaraokeLine())
        : null,
      parkedSourceBreaks: parked ? parked.syntheticBreaks : null,
      dubRecord: track.dubRecord,
    });
    return {
      clips,
      ...(dubTimed ? { dubTimed } : {}),
      configs: this.getKaraokeConfigs(),
      defaultConfigId: this.getDefaultKaraokeConfig()?.id || null,
    };
  }

  /**
   * PLAT-11905 — the dub this clip's voice plays, if any: the same answer the
   * dub audio is placed from.
   */
  getHeardDub(): HeardDub | null {
    const source = this.timeline.dubSource;
    if (this.externalHeardDub) {
      return resolveHeardDub({
        options: null,
        source,
        spans: [],
        external: this.externalHeardDub,
      });
    }
    const { spans } = readOriginalVideo(this.timeline, probe =>
      source ? source.isOriginalVideoElement(probe) : false,
    );
    return resolveHeardDub({
      options: this.timeline.dubOptions,
      source,
      spans,
    });
  }

  /** PLAT-11906 — let the Both voice position show the dub-timed captions. */
  setBothFollowsDub(value: boolean): void {
    if (this.bothFollowsDub === value) return;
    this.bothFollowsDub = value;
    this.syncCaptionSetToVoice({ reconcile: true });
  }

  /**
   * PLAT-11906 — the story-entry review plays the dub itself (not through this
   * editor's timeline) and says so here, with the stretch the dub covers; this
   * editor's captions then follow that dub. `null` when it stops.
   */
  setExternalHeardDub(dub: ExternalHeardDub | null): void {
    this.externalHeardDub = dub
      ? { language: dub.language, spans: dub.spans.map(span => ({ ...span })) }
      : null;
    this.syncCaptionSetToVoice({ reconcile: true });
  }

  /**
   * PLAT-11905 — show the caption set the voice calls for: the dub-timed set
   * exactly when the dubbed voice is heard in the captions' language
   * (`selectCaptionBasis`), the source-timed set otherwise.
   *
   * A switch moves the shown set aside and shows the other, building the
   * dub-timed record the first time it is needed, then re-fits the dub-timed
   * set (and only that set — the source-timed rows are left as they are). It touches
   * no style, writes no undo entry and marks nothing dirty: the saved shape is
   * the same whichever set is shown. With no caption track it does nothing, so
   * cleared captions never come back. `reconcile: true` also re-fits the
   * dub-timed set when it stays shown (a new description may carry new timing).
   */
  syncCaptionSetToVoice(options?: { reconcile?: boolean }): void {
    const track = this.getKaraokeTrack();
    if (!track) return;
    const config = this.getDefaultKaraokeConfig();
    const heard = this.getHeardDub();
    const wanted = selectCaptionBasis(
      config?.language,
      heard,
      // A dub the review plays itself is a dub being heard.
      this.externalHeardDub ? 'dubbed' : this.timeline.dubOptions?.voiceMode,
      // Under Both, Clip Preview, Creator Studio and publish keep source-timed
      // captions (PLAT-11904); only the review's editors follow the dub there.
      this.bothFollowsDub,
    );

    let switched = false;
    if (wanted !== track.liveKind) {
      if (wanted === 'dub' && heard && config) {
        if (
          !track.dubRecord ||
          normalizeLangKey(track.dubRecord.language) !==
            normalizeLangKey(heard.language)
        ) {
          track.dubRecord = buildDubRecord(
            heard.language,
            dubCaptionInputs(heard),
            dubRowOptions(config),
          );
        }
        track.showDubSet([]);
      } else {
        track.showSourceSet();
        // Styles stay with the language, not with a set: a style changed while
        // the dub-timed set was shown reaches the source rows too.
        const configs = this.getKaraokeConfigs();
        for (const clip of track.karaokeClips) {
          const current = configs.get(clip.karaokeConfig.id);
          if (current && current !== clip.karaokeConfig) {
            clip.setKaraokeConfig(current);
          }
        }
      }
      switched = true;
    }

    // Only the dub-timed set is re-fitted here. The source-timed set is kept
    // current by every ordinary reconcile, shown or parked, and is left exactly
    // as it was by a switch.
    if (switched || (options?.reconcile && track.liveKind === 'dub')) {
      new KaraokeTrackManager(this).reconcileDubCaptions();
    }
  }

  /**
   * PLAT-11905 — derive the shown dub-timed captions again, for a change the
   * editor does not see by itself: the captions' translation arriving or
   * changing, which each dub word's link to its fragment depends on. Returns
   * whether the dub-timed set is shown (and so was derived again).
   */
  refreshDubCaptions(): boolean {
    if (this.getKaraokeTrack()?.liveKind !== 'dub') return false;
    new KaraokeTrackManager(this).reconcileDubCaptions();
    return true;
  }

  /** PLAT-11905 — the shown caption of the dub-timed set with this id, if any. */
  private findDubCaption(clipId: string): KaraokeClip | undefined {
    const clip = this.getKaraokeTrack()?.karaokeClips.find(
      candidate => candidate.id === clipId,
    );
    return clip?.dubRowId ? clip : undefined;
  }

  /**
   * PLAT-11905 — a line break inside a dub-timed caption: the row is split in
   * its record at the place in the dub line the panel's position names.
   */
  private splitDubCaption(clip: KaraokeClip, textPosition: number): boolean {
    const rowId = clip.dubRowId;
    const at = clip.dubTiming
      ? dubCaptionSplitPoint(clip.dubTiming.words, textPosition)
      : null;
    if (!rowId || !at) return false;
    // Each dub route's undo label is the one the same gesture on any caption
    // shows (the existing command's own), so no new text reaches the screen.
    const label = new AddSyntheticCaptionBreakCommand(
      this,
      clip.id,
      [],
      textPosition,
    ).getDescription();
    return this.executeCommand(
      new EditDubCaptionRowsCommand(this, label, record =>
        splitDubRow(record, rowId, at),
      ),
    );
  }

  /**
   * PLAT-11905 — join a dub-timed caption onto the one above it, across dub
   * lines too (decided 2026-09-30: joins across dub lines are allowed, and the
   * joined row takes its time from both lines). Refused when the row above is
   * not a dub-timed caption, is the same row (two pieces of one row on either
   * side of a cut), or belongs to another speaker.
   */
  private joinDubCaption(right: KaraokeClip): boolean {
    const clips = this.getKaraokeTrack()?.karaokeClips ?? [];
    const index = clips.indexOf(right);
    const left = index > 0 ? clips[index - 1] : undefined;
    const leftRowId = left?.dubRowId;
    const rightRowId = right.dubRowId;
    if (!left || !leftRowId || !rightRowId || leftRowId === rightRowId) {
      return false;
    }
    if (captionClipsCrossSpeakers(left, right)) return false;
    const label = new MergeAdjacentCaptionClipsCommand(
      this,
      right.id,
    ).getDescription();
    return this.executeCommand(
      new EditDubCaptionRowsCommand(this, label, record =>
        joinDubRows(record, leftRowId, rightRowId),
      ),
    );
  }

  /**
   * PLAT-11905 — a dub-timed caption's time offsets are stored in its row
   * record, so they survive the next derive. They are checked exactly as any
   * caption's are (no overlap with its neighbours, a minimum length) by the
   * existing command, run on the shown caption, and the result is what the
   * record keeps. A row the clip shows in two pieces keeps a start offset only
   * on its first piece and an end offset only on its last, so a change the
   * other piece asks for is refused.
   */
  private adjustDubCaptionTime(
    clip: KaraokeClip,
    offset: { startSeconds?: number; endSeconds?: number },
  ): boolean {
    const rowId = clip.dubRowId;
    if (!rowId) return false;
    const pieces = (this.getKaraokeTrack()?.karaokeClips ?? []).filter(
      candidate => candidate.dubRowId === rowId,
    );
    const isFirst = pieces[0] === clip;
    const isLast = pieces[pieces.length - 1] === clip;
    // The timeline works both offsets out as differences, so the edge the
    // person did not move can carry a rounding residue; under a millisecond is
    // no change.
    const moved = (
      requested: number | undefined,
      current: number | undefined,
    ) =>
      requested !== undefined && Math.abs(requested - (current ?? 0)) >= 0.001;
    const changesStart = moved(offset.startSeconds, clip.startSecondsOffset);
    const changesEnd = moved(offset.endSeconds, clip.endSecondsOffset);
    if ((changesStart && !isFirst) || (changesEnd && !isLast)) return false;

    const check = new AdjustKaraokeClipTimeCommand(this, clip.id, offset, true);
    if (!check.execute()) return false;
    const startSecondsOffset = clip.startSecondsOffset;
    const endSecondsOffset = clip.endSecondsOffset;
    const stored = this.executeCommand(
      new EditDubCaptionRowsCommand(this, check.getDescription(), record => {
        const row = record.rows.find(candidate => candidate.id === rowId);
        if (!row) return null;
        return setDubRowOffsets(record, rowId, {
          startSecondsOffset: isFirst
            ? startSecondsOffset
            : row.startSecondsOffset,
          endSecondsOffset: isLast ? endSecondsOffset : row.endSecondsOffset,
        });
      }),
    );
    if (!stored) check.undo();
    return stored;
  }

  /**
   * Set default karaoke config by creating a new config or using existing one
   * @param config - Karaoke config without ID
   */
  setDefaultKaraokeConfig(config: Omit<KaraokeConfig, 'id'>): void {
    // Create a new config with the provided settings
    const newConfig = this.createKaraokeConfig(config);
    this.defaultKaraokeConfigId = newConfig.id;
    VideoEditorLogger.logKaraoke('set_default_karaoke_config', {
      configId: newConfig.id,
    });
  }

  /**
   * Get the default karaoke config
   * @returns The default KaraokeConfig or null if not set
   */
  getDefaultKaraokeConfig(): KaraokeConfig | null {
    if (!this.defaultKaraokeConfigId) {
      return null;
    }
    return this.karaokeConfigs.get(this.defaultKaraokeConfigId) || null;
  }

  setTranslationPhraseResolver(
    resolver: TranslationPhraseResolver | undefined,
  ): void {
    this.translationPhraseResolver = resolver;
  }

  getTranslationPhrasesForLanguage(
    language: string,
  ): readonly TranslationPhrase[] | null {
    if (!this.translationPhraseResolver) return null;
    return this.translationPhraseResolver(language);
  }

  /**
   * Create a new karaoke config
   * @param config - Karaoke config without ID
   * @returns The newly created KaraokeConfig with generated ID
   */
  createKaraokeConfig(config: Omit<KaraokeConfig, 'id'>): KaraokeConfig {
    // Get the KaraokeConfig ID generator (creates it if doesn't exist)
    const generator = IdGenerator.get(IdGeneratorEntityType.KaraokeConfig);
    const id = String(generator.getNextId());
    this.karaokeConfigs.set(id, { ...config, id });
    VideoEditorLogger.logKaraoke('create_karaoke_config', {
      configId: id,
    });
    return { ...config, id };
  }

  /**
   * Update an existing karaoke config
   * @param config - The karaoke config to update
   * @returns true if successful, false if config doesn't exist
   */
  updateKaraokeConfig(config: KaraokeConfig): boolean {
    if (!this.karaokeConfigs.has(config.id)) {
      VideoEditorLogger.logKaraoke('update_karaoke_config_error', {
        configId: config.id,
        error: 'Config does not exist',
      });
      console.warn(`Karaoke config with ID ${config.id} does not exist`);
      return false;
    }
    this.karaokeConfigs.set(config.id, config);
    VideoEditorLogger.logKaraoke('update_karaoke_config', {
      configId: config.id,
    });
    return true;
  }

  /**
   * Delete a karaoke config
   * @param configId - ID of the config to delete
   * @returns true if successful, false if config doesn't exist or is the default
   */
  deleteKaraokeConfig(configId: string): boolean {
    if (configId === this.defaultKaraokeConfigId) {
      VideoEditorLogger.logKaraoke('delete_karaoke_config_error', {
        configId,
        error: 'Cannot delete default config',
      });
      console.warn('Cannot delete the default karaoke config');
      return false;
    }
    if (!this.karaokeConfigs.has(configId)) {
      VideoEditorLogger.logKaraoke('delete_karaoke_config_error', {
        configId,
        error: 'Config does not exist',
      });
      console.warn(`Karaoke config with ID ${configId} does not exist`);
      return false;
    }
    const result = this.karaokeConfigs.delete(configId);
    VideoEditorLogger.logKaraoke('delete_karaoke_config', {
      configId,
      success: result,
    });
    return result;
  }

  /**
   * Get the karaoke track from the timeline
   * @returns The KaraokeTrack or undefined if not found
   */
  getKaraokeTrack(): KaraokeTrack | undefined {
    const track = this.timeline.getTrack(String(KARAOKE_TRACK_NUMBER));
    if (track instanceof KaraokeTrack) {
      return track;
    }
    return undefined;
  }

  hasKaraokeTrack(): boolean {
    return !!this.timeline.getTrack(String(KARAOKE_TRACK_NUMBER));
  }

  commandReplaceTranscriptText(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    newTextValue: string,
  ): boolean {
    if (fromSegmentId === toSegmentId) {
      const command = new TranscriptTextReplaceCommand(
        this,
        fromSegmentId,
        fromIndex,
        toIndex,
        newTextValue,
      );
      return this.executeCommand(command);
    } else {
      const command = new TranscriptMultiSegmentTextReplaceCommand(
        this,
        fromSegmentId,
        fromIndex,
        toSegmentId,
        toIndex,
        newTextValue,
      );
      return this.executeCommand(command);
    }
  }

  /**
   * Reassign speaker labels on transcript tokens within a segment range.
   * For multi-segment ranges, iterates each segment and executes in a batch.
   */
  commandReassignSpeakerSection(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    newSpeakerLabel: string,
  ): boolean {
    return this.executeSpeakerReassignAcrossSegments(
      fromSegmentId,
      fromIndex,
      toSegmentId,
      toIndex,
      newSpeakerLabel,
      false,
    );
  }

  /**
   * Undo a speaker section reassignment by restoring original speaker labels.
   */
  commandUndoReassignSpeakerSection(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
  ): boolean {
    return this.executeSpeakerReassignAcrossSegments(
      fromSegmentId,
      fromIndex,
      toSegmentId,
      toIndex,
      '',
      true,
    );
  }

  private executeSpeakerReassignAcrossSegments(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    newSpeakerLabel: string,
    restoreOriginal: boolean,
  ): boolean {
    if (fromSegmentId === toSegmentId) {
      const command = new TranscriptSpeakerReassignCommand(
        this,
        fromSegmentId,
        fromIndex,
        toIndex,
        newSpeakerLabel,
        restoreOriginal,
      );
      return this.executeCommand(command);
    }

    const transcriptTrack = this.getVideoTranscriptTrack();
    if (!transcriptTrack) return false;

    const allElements = transcriptTrack.transcriptSegmentElements;
    const fromElementIndex = allElements.findIndex(
      el => el.getClipTranscriptSegment().id === fromSegmentId,
    );
    const toElementIndex = allElements.findIndex(
      el => el.getClipTranscriptSegment().id === toSegmentId,
    );
    if (fromElementIndex === -1 || toElementIndex === -1) return false;

    // Normalize order so we always iterate from earlier to later segment
    const startElementIndex = Math.min(fromElementIndex, toElementIndex);
    const endElementIndex = Math.max(fromElementIndex, toElementIndex);

    return this.executeInBatch(() => {
      let success = true;
      for (let i = startElementIndex; i <= endElementIndex; i++) {
        const element = allElements[i];
        const segment = element.getClipTranscriptSegment();
        const segmentId = segment.id;
        const tokenCount = segment.getWordsWithPunctuation().length;

        // Match by segment ID so fromIndex always pairs with fromSegmentId
        // regardless of iteration direction (same pattern as commandRemoveTranscript)
        const startIdx = segmentId === fromSegmentId ? fromIndex : 0;
        const endIdx = segmentId === toSegmentId ? toIndex : tokenCount - 1;

        const command = new TranscriptSpeakerReassignCommand(
          this,
          segmentId,
          startIdx,
          endIdx,
          newSpeakerLabel,
          restoreOriginal,
        );
        const result = this.executeCommand(command);
        success = success && result;
      }
      return success;
    });
  }

  /**
   * Replace multiple disjoint ranges within a single segment in one command.
   * Falls back to single-range command if only one change provided.
   */
  commandReplaceTranscriptTextMultiRanges(
    segmentId: string,
    changes: SegmentTextChange[],
  ): boolean {
    if (!changes.length) return false;
    if (changes.length === 1) {
      const c = changes[0];
      return this.commandReplaceTranscriptText(
        segmentId,
        c.fromIndex,
        segmentId,
        c.toIndex,
        c.newText,
      );
    }
    const command = new TranscriptMultiRangeTextReplaceCommand(
      this,
      segmentId,
      changes,
    );
    return this.executeCommand(command);
  }

  commandEditTranscriptTime(
    /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars*/
    transcriptTokenId: string,
    newMediaTime: { startTime: number; endTime: number },
    /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars*/
  ): boolean {
    throw new Error('Method not implemented.');
  }

  commandAddTranscriptPunctuation(
    code: KnownPunctuation,
    segmentId: string,
    afterIndex: number,
  ): boolean {
    const command = new TranscriptAddPunctuationCommand(
      this,
      code,
      segmentId,
      afterIndex,
    );
    return this.executeCommand(command);
  }

  commandUpdateKaraokeConfig(
    config: KaraokeConfig,
    createUndoRedoEntry: boolean = true,
  ): boolean {
    const command = new UpdateKaraokeConfigCommand(
      this,
      config,
      createUndoRedoEntry,
    );
    return this.executeCommand(command);
  }

  commandProduceKaraoke(options?: {
    replaceTrack: boolean;
    createUndoRedoEntry?: boolean;
  }): boolean {
    // Check if any track already occupies the karaoke slot. Use the raw
    // timeline lookup (not getKaraokeTrack) so a plain Track reconstructed
    // from persisted karaoke elements — which is NOT a KaraokeTrack instance
    // until loadKaraokeData rehydrates it — is still detected and replaced.
    // Otherwise getKaraokeTrack() returns undefined, the removal below is
    // skipped, and addExistingTrack throws "A track with ID 32 already exists"
    // (slot 32 is reserved for karaoke, so any track there is safe to remove).
    const existingKaraokeTrack = this.timeline.getTrack(
      String(KARAOKE_TRACK_NUMBER),
    );
    if (existingKaraokeTrack && !options?.replaceTrack) {
      console.error('Cannot produce karaoke: KaraokeTrack already exists');
      return false;
    }

    if (existingKaraokeTrack) {
      // TODO: when only config should be changed, do not re-produce, just re-render
      // Remove existing karaoke track
      this.timeline.removeTrack(String(KARAOKE_TRACK_NUMBER));
      console.debug('Removed existing karaoke track');
    }

    const command = new ProduceKaraokeCommand(
      this,
      KARAOKE_TRACK_NUMBER,
      options?.createUndoRedoEntry,
    );
    const produced = this.executeCommand(command);
    // PLAT-11689 — `hidden` hides the track on screen. With no track it is a
    // leftover (a Clear, or a video loaded into this reused editor), so a track
    // produced from nothing starts shown; regenerating one keeps it.
    if (produced && !existingKaraokeTrack) {
      setCaptionsHidden(this, false, { createUndoRedoEntry: false });
    }
    return produced;
  }

  commandApplyKaraokeConfig(
    /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars*/
    config: { id: string } | Omit<KaraokeConfig, 'id'>,
    karaokeClipIds?: string[],
    /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars*/
  ): boolean {
    throw new Error('Method not implemented.');
  }

  commandAddBreak(transcriptTokenId: string): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot add breaks: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new AddKaraokeBreakCommand(this, transcriptTokenId);
    return this.executeCommand(command);
  }

  commandRemoveBreak(transcriptTokenId: string): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot remove breaks: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new RemoveKaraokeBreakCommand(this, transcriptTokenId);
    return this.executeCommand(command);
  }

  /**
   * PLAT-11914: add a soft line break inside a caption (the caption stays
   * one caption; the text after the break renders on the next line), or
   * remove the one already at `anchor`.
   */
  commandToggleCaptionSoftBreak(anchor: CaptionSoftBreakAnchor): boolean {
    if (!this.getKaraokeTrack()) return false;
    const command = new ToggleCaptionSoftBreakCommand(this, anchor);
    return this.executeCommand(command);
  }

  /**
   * PLAT-9623: insert a synthetic line break inside the single-phrase
   * translated clip identified by `clipId`. `phraseOriginalIndices`
   * identifies the source phrase; `textPosition` is the char offset into
   * the phrase's translated text. Time placement is derived via the
   * active SplitStrategy.
   */
  commandAddSyntheticCaptionBreak(
    clipId: string,
    phraseOriginalIndices: number[],
    textPosition: number,
  ): boolean {
    if (!this.getKaraokeTrack()) {
      logger.warn(
        "Cannot add synthetic caption break: KaraokeTrack doesn't exist",
      );
      return false;
    }
    // PLAT-11905 — a dub-timed caption's breaks live in its row record.
    const dubCaption = this.findDubCaption(clipId);
    if (dubCaption) return this.splitDubCaption(dubCaption, textPosition);
    const command = new AddSyntheticCaptionBreakCommand(
      this,
      clipId,
      phraseOriginalIndices,
      textPosition,
    );
    return this.executeCommand(command);
  }

  /**
   * PLAT-9623: move an existing synthetic break to a new text position
   * (used by the drag handle). Pass `createUndoRedoEntry: false` for
   * live drag-move steps; emit a final call with `createUndoRedoEntry:
   * true` (the default) on drag-end.
   */
  commandAdjustSyntheticCaptionBreak(
    breakId: string,
    nextTextPosition: number,
    options?: { createUndoRedoEntry?: boolean },
  ): boolean {
    if (!this.getKaraokeTrack()) return false;
    const command = new AdjustSyntheticCaptionBreakCommand(
      this,
      breakId,
      nextTextPosition,
      options,
    );
    return this.executeCommand(command);
  }

  /**
   * PLAT-9623: drop a synthetic break by id and merge the adjacent
   * sub-clips back into one (or fewer if other breaks remain on the
   * same phrase).
   */
  commandRemoveSyntheticCaptionBreak(breakId: string): boolean {
    if (!this.getKaraokeTrack()) return false;
    const command = new RemoveSyntheticCaptionBreakCommand(this, breakId);
    return this.executeCommand(command);
  }

  /**
   * PLAT-9623: merge an adjacent caption clip pair back into one
   * (inverse of `commandAddSyntheticCaptionBreak`). Pass the right
   * clip's id; the command pulls in the left neighbor and merges.
   *
   * Returns false when it refuses: there is no caption track, the clip
   * has no left neighbour, or (PLAT-10945) the two clips belong to
   * different speakers, which would mix two people's words into one
   * caption row.
   *
   * This comment previously claimed the command "refuses across natural
   * producer boundaries (different token arrays)". It does the opposite —
   * it concatenates the two token arrays. Corrected 2026-07-30.
   */
  commandMergeAdjacentCaptionClips(rightClipId: string): boolean {
    if (!this.getKaraokeTrack()) return false;
    // PLAT-11905 — dub-timed rows are joined in their row record.
    const dubCaption = this.findDubCaption(rightClipId);
    if (dubCaption) return this.joinDubCaption(dubCaption);
    const command = new MergeAdjacentCaptionClipsCommand(this, rightClipId);
    return this.executeCommand(command);
  }

  /**
   * Re-text the existing translated karaoke track from the current translation
   * phrases, without re-segmenting it. Use this — NOT
   * `commandProduceKaraoke` — to propagate a translation edit into baked
   * captions: a re-produce rebuilds from the producer's segmentation and
   * discards the user's synthetic line breaks and merged rows (a merge leaves
   * no record to replay).
   */
  commandSyncTranslatedCaptionText(
    createUndoRedoEntry: boolean = true,
  ): boolean {
    if (!this.getKaraokeTrack()) return false;
    // PLAT-11905 — re-texting is for the source-timed rows, shown or parked;
    // the dub-timed rows keep the voice's words.
    const command = new SourceSetScopeCommand(
      this,
      new SyncTranslatedCaptionTextCommand(this, createUndoRedoEntry),
    );
    return this.executeCommand(command);
  }

  /**
   * PLAT-9623: drop every synthetic-break record on the karaoke track.
   * Used by the captions panel's "Reset captions" action so a follow-up
   * `commandProduceKaraoke` starts from the producer's default
   * segmentation, not the post-split state.
   */
  commandClearSyntheticCaptionBreaks(): boolean {
    if (!this.getKaraokeTrack()) return false;
    const command = new ClearSyntheticCaptionBreaksCommand(this);
    return this.executeCommand(command);
  }

  commandAdjustKaraokeClipTime(
    karaokeClipId: string,
    offset: { startSeconds?: number; endSeconds?: number },
  ): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error(
        "Cannot adjust karaoke clip time: KaraokeTrack doesn't exist",
      );
      return false;
    }
    // PLAT-11905 — a dub-timed caption's offsets live in its row record, so an
    // offset written only on the shown clip would be lost on the next derive.
    const dubCaption = this.findDubCaption(karaokeClipId);
    if (dubCaption) return this.adjustDubCaptionTime(dubCaption, offset);
    const command = new AdjustKaraokeClipTimeCommand(
      this,
      karaokeClipId,
      offset,
      true,
    );
    return this.executeCommand(command);
  }

  commandHideInKaraoke(transcriptTokenIds: string[]): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot hide in karaoke: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new HideInKaraokeCommand(this, transcriptTokenIds);
    return this.executeCommand(command);
  }

  commandShowInKaraoke(transcriptTokenIds: string[]): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot show in karaoke: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new ShowInKaraokeCommand(this, transcriptTokenIds);
    return this.executeCommand(command);
  }

  /**
   * Begins a batch of operations, deferring renderer synchronization and observer notifications.
   * Maintains backward compatibility with the depth counter approach while using a simpler boolean flag.
   */
  beginBatch(): void {
    this.batchDepth++;
    this.inBatch = true;

    // Log batch operation
    VideoEditorLogger.logBatch('begin', this.batchDepth);
  }

  /**
   * Ends a batch of operations. Only syncs to renderer and processes notifications at the outermost batch level.
   * Maintains backward compatibility with the depth counter approach while using a simpler boolean flag.
   * @returns true if this was the outermost batch that completed, false for nested batches
   */
  endBatch(): boolean {
    if (this.batchDepth <= 0 || !this.inBatch) {
      throw new Error('No active batch to end');
    }

    // Decrement the batch depth counter
    const oldDepth = this.batchDepth;
    this.batchDepth--;

    // Log batch operation
    VideoEditorLogger.logBatch('end', this.batchDepth, {
      previousDepth: oldDepth,
    });

    // Only take action at the outermost level
    if (this.batchDepth === 0) {
      // Reset the inBatch flag at the outermost level
      this.inBatch = false;

      // Performance timing for syncing
      const syncStart = performance.now();
      let syncDuration = 0;

      // Only sync if changes were made during the batch
      if (this.dirty) {
        this.dirty = false;
        syncDuration = performance.now() - syncStart;

        // Log performance of sync operation
        VideoEditorLogger.logPerformance('renderer_sync', syncDuration);
      }

      // Add a batch completion notification to the queue
      this.observerManager.queueNotification(
        EditorChangeType.TIMELINE_CHANGED,
        {
          source: 'batch_complete',
          changes: 'multiple_operations',
        },
      );

      // Process all queued notifications after batch is complete and sync is done
      const notifStart = performance.now();
      this.observerManager.processQueuedNotifications();
      const notifDuration = performance.now() - notifStart;

      // Log performance of notification processing
      VideoEditorLogger.logPerformance('process_notifications', notifDuration);

      return true;
    }

    return false;
  }

  /**
   * Alias for endBatch to maintain compatibility
   */
  commitBatch(): boolean {
    return this.endBatch();
  }

  /**
   * Aborts the current batch operation, restoring the batch state without syncing
   */
  abortBatch(): void {
    if (this.batchDepth <= 0 || !this.inBatch) {
      throw new Error('No active batch to abort');
    }

    const oldDepth = this.batchDepth;

    // Log batch abort
    VideoEditorLogger.logBatch('abort', this.batchDepth, {
      reason: 'explicit_abort',
      previousDepth: oldDepth,
    });

    // Reset to no batching state
    this.batchDepth = 0;
    this.inBatch = false;
  }

  /**
   * Returns whether the editor is currently syncing to the renderer
   * Used by ObserverManager to determine if notifications should be queued
   */
  isSyncingToRenderer(): boolean {
    return this._isSyncingToRenderer;
  }

  /**
   * Sets a track as the designated transcript track
   * @param trackId The ID of the track to set as transcript track
   * @returns boolean indicating success
   */
  setTranscriptTrack(trackId: string): boolean {
    const track = this.getTrackById(trackId);
    if (!track) {
      VideoEditorLogger.logTranscriptOperation('set_transcript_track_error', {
        error: 'track_not_found',
        trackId,
      });
      return false;
    }

    if (!(track instanceof TranscriptTrack)) {
      VideoEditorLogger.logTranscriptOperation('set_transcript_track_error', {
        error: 'not_transcript_track',
        trackId,
        expectedClass: 'TranscriptTrack',
        actualType: track.type,
        actualClass: track.constructor.name,
      });
      return false;
    }

    this.transcriptTrackId = trackId;
    VideoEditorLogger.logTranscriptOperation('set_transcript_track', {
      trackId,
    });
    return true;
  }

  /**
   * Gets the current transcript track
   * @returns The transcript track or undefined if not set
   */
  getTranscriptTrack(): Track | undefined {
    if (!this.transcriptTrackId) return undefined;
    return this.getTrackById(this.transcriptTrackId);
  }

  getVideoTranscriptTrack(): TranscriptTrack | undefined {
    // return first video track for now
    return this.timeline.tracks.find(
      track => track instanceof TranscriptTrack,
    ) as TranscriptTrack | undefined;
  }

  public getElementAtTime(
    trackId: string,
    time: number,
  ): Record<string, any> | null {
    // Find the element in the source that matches the given time
    const track = this.timeline.getTrack(trackId);
    if (!track) {
      VideoEditorLogger.logTimelineOperation('get_element_at_time_error', {
        error: 'track_not_found',
        trackId,
        time,
      });
      throw new Error(`Track with ID ${trackId} does not exist`);
    }

    const elements = track.elements;
    const element = elements.find(el => {
      return el.time <= time && time < el.end_time;
    });

    if (!element) {
      VideoEditorLogger.logTimelineOperation('get_element_at_time', {
        status: 'not_found',
        trackId,
        time,
        elementsInTrack: elements.length,
      });
      return null;
    }

    VideoEditorLogger.logTimelineOperation('get_element_at_time', {
      status: 'found',
      trackId,
      time,
      elementId: element.id,
      elementType: element.type,
    });
    return element || null;
  }

  public passExtraElementData(extraElementData: Video['extraElementData']) {
    this.trackManager.extraElementDataRaw = extraElementData;
  }

  /**
   * Re-runs placement reconciliation outside the command flow. Used when a
   * graphic is re-pinned and must snap back to what its placement says — the
   * pin state changed, but no timeline edit happened to carry the reconcile.
   * Wrapped in a batch so the repositioned time syncs to the renderer like a
   * normal command would.
   */
  public reconcilePlacements(): void {
    this.executeInBatch(() => {
      new PlacementReconciler(this).reconcile();
      this.timeline.recalculateDuration();
      this.markDirty('reconcile_placements');
    });
  }

  public passPunchListItems(items: PunchListItemData[]): void {
    const fullTranscript = this.getFullTranscript();
    if (fullTranscript && fullTranscript.length > 0) {
      // Transcript is loaded, hydrate immediately
      this.trackManager.hydratePunchListItems(fullTranscript, items);
      this.pendingPunchListItems = null; // Clear any pending items
    } else {
      // Transcript not loaded yet, store items for later hydration
      this.pendingPunchListItems = items;
    }
  }

  public isTranscriptTrackElement(elementId: string): boolean {
    const element = this.getElementById(elementId);
    if (!element || !Element.isVideoElement(element)) {
      return false;
    }
    return this.getTranscriptTrack()?.id === element.track?.toString();
  }

  public setFlag(flag: KnownFlags, value: boolean): void {
    console.log('VideoEditor: set flag', flag, value);
    this.flags[flag] = value;
  }

  public isFlagOn(flag: KnownFlags): boolean {
    return this.flags[flag] ?? false;
  }

  public getConfig(): VideoEditorConfig {
    return this.config;
  }

  public updateConfig(config: Partial<VideoEditorConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
